import * as z from 'zod';

export const ListCatalogsRequest = z.object({
  options: z
    .object({
      sort: z
        .object({
          fieldName: z
            .string()
            .describe('Name of the field to sort by.')
            .max(512)
            .optional(),
          order: z.enum(['ASC', 'DESC']).optional(),
        })
        .describe('Sort order. Defaults to `ASC`.')
        .optional(),
      paging: z
        .object({
          limit: z
            .number()
            .int()
            .describe('Number of items to load.')
            .min(0)
            .optional()
            .nullable(),
          offset: z
            .number()
            .int()
            .describe('Number of items to skip in the current sort order.')
            .min(0)
            .optional()
            .nullable(),
        })
        .describe(
          'Paging information. `offset` defaults to `0` and `limit` defaults to `50`. The maximum for limit is `1,000`.'
        )
        .optional(),
      archived: z
        .boolean()
        .describe(
          'Whether archived catalogs are returned. Defaults to `false`.'
        )
        .optional(),
      locationIds: z.array(z.string()).optional(),
    })
    .optional(),
});
export const ListCatalogsResponse = z.object({
  catalogs: z
    .array(
      z.object({
        _id: z.string().describe('Catalog ID.').optional().nullable(),
        locationId: z
          .string()
          .describe(
            'ID of the location the catalog belongs to. See the [Locations API](https://dev.wix.com/api/rest/business-info/locations/introduction) for more details.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        archived: z
          .boolean()
          .describe(
            "Whether the catalog is archived.\n__Note:__ Archived catalogs can't be updated."
          )
          .optional(),
        draftPublishedDate: z
          .date()
          .describe(
            'Date and time the last time a draft catalog has been published in `yyyy-mm-ddThh:mm:sssZ` format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
          )
          .optional()
          .nullable(),
      })
    )
    .optional(),
});
export const GetMenuRequest = z.object({
  identifiers: z.object({
    catalogId: z
      .string()
      .describe('ID of the catalog the menu belongs to.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
    menuId: z.string().describe('Menu ID.'),
  }),
});
export const GetMenuResponse = z.object({
  menu: z
    .object({
      _id: z.string().describe('Menu ID.').optional().nullable(),
      name: z.string().describe('Menu name.').optional().nullable(),
      description: z
        .string()
        .describe('Menu description.')
        .optional()
        .nullable(),
      imageUrl: z
        .string()
        .describe("URL of the menu's image file.")
        .optional()
        .nullable(),
      visibilityCriteria: z
        .object({
          visible: z
            .boolean()
            .describe(
              "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
            )
            .optional()
            .nullable(),
          fulfillmentTypes: z
            .array(
              z.enum([
                'UNSPECIFIED_FULFILLMENT_TYPE',
                'DELIVERY',
                'PICKUP_OR_DINE_IN',
              ])
            )
            .min(1)
            .max(2)
            .optional(),
          platforms: z
            .array(
              z.enum([
                'UNSPECIFIED_PLATFORM',
                'SITE',
                'MOBILE_SITE',
                'WIX_APP',
                'CALL_CENTER',
                'CHAT_BOT',
              ])
            )
            .min(1)
            .max(5)
            .optional(),
          availability: z
            .object({
              periods: z
                .array(
                  z.object({
                    openDay: z
                      .enum([
                        'UNDEFINED',
                        'SUN',
                        'MON',
                        'TUE',
                        'WED',
                        'THU',
                        'FRI',
                        'SAT',
                      ])
                      .describe('Day of the week the period starts on.')
                      .optional(),
                    openTime: z
                      .string()
                      .describe(
                        'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                      )
                      .optional(),
                    closeDay: z
                      .enum([
                        'UNDEFINED',
                        'SUN',
                        'MON',
                        'TUE',
                        'WED',
                        'THU',
                        'FRI',
                        'SAT',
                      ])
                      .describe('Day of the week the period ends on.')
                      .optional(),
                    closeTime: z
                      .string()
                      .describe(
                        'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                      )
                      .optional(),
                  })
                )
                .optional(),
              specialHourPeriods: z
                .array(
                  z.object({
                    startDate: z
                      .string()
                      .describe(
                        'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                      )
                      .optional(),
                    endDate: z
                      .string()
                      .describe(
                        'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                      )
                      .optional(),
                    available: z
                      .boolean()
                      .describe(
                        'Whether the item is available during the exception. Defaults to `true`.'
                      )
                      .optional(),
                    eventName: z
                      .string()
                      .describe(
                        'Name of the special hour period. In the dashboard, the special hour period is called event.'
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .optional(),
            })
            .describe('Time periods when the entity is available.')
            .optional(),
        })
        .describe(
          'Visibility criteria that must be met for the menu to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
        )
        .optional(),
      sectionIds: z
        .object({ values: z.array(z.string()).optional() })
        .describe('IDs of the sections that are included in the menu.')
        .optional(),
      archived: z
        .boolean()
        .describe(
          "Whether the menu is archived. Defaults to `false`. **Note:** Archived menus can't be updated."
        )
        .optional()
        .nullable(),
    })
    .describe('Retrieved menu.')
    .optional(),
});
export const GetSectionRequest = z.object({
  identifiers: z.object({
    catalogId: z
      .string()
      .describe('ID of the catalog the section belongs to.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
    menuId: z.string().describe('ID of the menu the catalog belongs to.'),
    sectionId: z.string().describe('Section ID.'),
  }),
});
export const GetSectionResponse = z.object({
  section: z
    .object({
      _id: z.string().describe('Section ID.').optional().nullable(),
      menuId: z
        .string()
        .describe('ID of the menu the section belongs to.')
        .optional()
        .nullable(),
      name: z.string().describe('Section name.').optional().nullable(),
      description: z
        .string()
        .describe('Section description.')
        .optional()
        .nullable(),
      imageUrl: z
        .string()
        .describe("URL of the section's image file.")
        .optional()
        .nullable(),
      itemIds: z
        .object({ values: z.array(z.string()).optional() })
        .describe('IDs of the items that belong to the section.')
        .optional(),
      visibilityCriteria: z
        .object({
          visible: z
            .boolean()
            .describe(
              "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
            )
            .optional()
            .nullable(),
          fulfillmentTypes: z
            .array(
              z.enum([
                'UNSPECIFIED_FULFILLMENT_TYPE',
                'DELIVERY',
                'PICKUP_OR_DINE_IN',
              ])
            )
            .min(1)
            .max(2)
            .optional(),
          platforms: z
            .array(
              z.enum([
                'UNSPECIFIED_PLATFORM',
                'SITE',
                'MOBILE_SITE',
                'WIX_APP',
                'CALL_CENTER',
                'CHAT_BOT',
              ])
            )
            .min(1)
            .max(5)
            .optional(),
          availability: z
            .object({
              periods: z
                .array(
                  z.object({
                    openDay: z
                      .enum([
                        'UNDEFINED',
                        'SUN',
                        'MON',
                        'TUE',
                        'WED',
                        'THU',
                        'FRI',
                        'SAT',
                      ])
                      .describe('Day of the week the period starts on.')
                      .optional(),
                    openTime: z
                      .string()
                      .describe(
                        'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                      )
                      .optional(),
                    closeDay: z
                      .enum([
                        'UNDEFINED',
                        'SUN',
                        'MON',
                        'TUE',
                        'WED',
                        'THU',
                        'FRI',
                        'SAT',
                      ])
                      .describe('Day of the week the period ends on.')
                      .optional(),
                    closeTime: z
                      .string()
                      .describe(
                        'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                      )
                      .optional(),
                  })
                )
                .optional(),
              specialHourPeriods: z
                .array(
                  z.object({
                    startDate: z
                      .string()
                      .describe(
                        'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                      )
                      .optional(),
                    endDate: z
                      .string()
                      .describe(
                        'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                      )
                      .optional(),
                    available: z
                      .boolean()
                      .describe(
                        'Whether the item is available during the exception. Defaults to `true`.'
                      )
                      .optional(),
                    eventName: z
                      .string()
                      .describe(
                        'Name of the special hour period. In the dashboard, the special hour period is called event.'
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .optional(),
            })
            .describe('Time periods when the entity is available.')
            .optional(),
        })
        .describe(
          'Visibility criteria that must be met for the section to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
        )
        .optional(),
      archived: z
        .boolean()
        .describe(
          "Whether the section is archived. Defaults to `false`. **Note:** Archived sections can't be updated."
        )
        .optional()
        .nullable(),
    })
    .describe('Retrieved section.')
    .optional(),
});
export const UpdateMenuRequest = z.object({
  identifiers: z.object({
    catalogId: z
      .string()
      .describe('ID of the catalog the menu belongs to.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
    menuId: z.string().describe('Menu ID.'),
  }),
  options: z
    .object({
      menu: z
        .object({
          _id: z.string().describe('Menu ID.').optional().nullable(),
          name: z.string().describe('Menu name.').optional().nullable(),
          description: z
            .string()
            .describe('Menu description.')
            .optional()
            .nullable(),
          imageUrl: z
            .string()
            .describe("URL of the menu's image file.")
            .optional()
            .nullable(),
          visibilityCriteria: z
            .object({
              visible: z
                .boolean()
                .describe(
                  "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
                )
                .optional()
                .nullable(),
              fulfillmentTypes: z
                .array(
                  z.enum([
                    'UNSPECIFIED_FULFILLMENT_TYPE',
                    'DELIVERY',
                    'PICKUP_OR_DINE_IN',
                  ])
                )
                .min(1)
                .max(2)
                .optional(),
              platforms: z
                .array(
                  z.enum([
                    'UNSPECIFIED_PLATFORM',
                    'SITE',
                    'MOBILE_SITE',
                    'WIX_APP',
                    'CALL_CENTER',
                    'CHAT_BOT',
                  ])
                )
                .min(1)
                .max(5)
                .optional(),
              availability: z
                .object({
                  periods: z
                    .array(
                      z.object({
                        openDay: z
                          .enum([
                            'UNDEFINED',
                            'SUN',
                            'MON',
                            'TUE',
                            'WED',
                            'THU',
                            'FRI',
                            'SAT',
                          ])
                          .optional(),
                        openTime: z
                          .string()
                          .describe(
                            'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                          )
                          .optional(),
                        closeDay: z
                          .enum([
                            'UNDEFINED',
                            'SUN',
                            'MON',
                            'TUE',
                            'WED',
                            'THU',
                            'FRI',
                            'SAT',
                          ])
                          .optional(),
                        closeTime: z
                          .string()
                          .describe(
                            'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                          )
                          .optional(),
                      })
                    )
                    .optional(),
                  specialHourPeriods: z
                    .array(
                      z.object({
                        startDate: z
                          .string()
                          .describe(
                            'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                          )
                          .optional(),
                        endDate: z
                          .string()
                          .describe(
                            'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                          )
                          .optional(),
                        available: z
                          .boolean()
                          .describe(
                            'Whether the item is available during the exception. Defaults to `true`.'
                          )
                          .optional(),
                        eventName: z
                          .string()
                          .describe(
                            'Name of the special hour period. In the dashboard, the special hour period is called event.'
                          )
                          .optional()
                          .nullable(),
                      })
                    )
                    .optional(),
                })
                .describe('Time periods when the entity is available.')
                .optional(),
            })
            .describe(
              'Visibility criteria that must be met for the menu to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
            )
            .optional(),
          sectionIds: z
            .object({ values: z.array(z.string()).optional() })
            .describe('IDs of the sections that are included in the menu.')
            .optional(),
          archived: z
            .boolean()
            .describe(
              "Whether the menu is archived. Defaults to `false`. **Note:** Archived menus can't be updated."
            )
            .optional()
            .nullable(),
        })
        .describe('Menu to update.')
        .optional(),
    })
    .optional(),
});
export const UpdateMenuResponse = z.object({
  menu: z
    .object({
      _id: z.string().describe('Menu ID.').optional().nullable(),
      name: z.string().describe('Menu name.').optional().nullable(),
      description: z
        .string()
        .describe('Menu description.')
        .optional()
        .nullable(),
      imageUrl: z
        .string()
        .describe("URL of the menu's image file.")
        .optional()
        .nullable(),
      visibilityCriteria: z
        .object({
          visible: z
            .boolean()
            .describe(
              "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
            )
            .optional()
            .nullable(),
          fulfillmentTypes: z
            .array(
              z.enum([
                'UNSPECIFIED_FULFILLMENT_TYPE',
                'DELIVERY',
                'PICKUP_OR_DINE_IN',
              ])
            )
            .min(1)
            .max(2)
            .optional(),
          platforms: z
            .array(
              z.enum([
                'UNSPECIFIED_PLATFORM',
                'SITE',
                'MOBILE_SITE',
                'WIX_APP',
                'CALL_CENTER',
                'CHAT_BOT',
              ])
            )
            .min(1)
            .max(5)
            .optional(),
          availability: z
            .object({
              periods: z
                .array(
                  z.object({
                    openDay: z
                      .enum([
                        'UNDEFINED',
                        'SUN',
                        'MON',
                        'TUE',
                        'WED',
                        'THU',
                        'FRI',
                        'SAT',
                      ])
                      .describe('Day of the week the period starts on.')
                      .optional(),
                    openTime: z
                      .string()
                      .describe(
                        'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                      )
                      .optional(),
                    closeDay: z
                      .enum([
                        'UNDEFINED',
                        'SUN',
                        'MON',
                        'TUE',
                        'WED',
                        'THU',
                        'FRI',
                        'SAT',
                      ])
                      .describe('Day of the week the period ends on.')
                      .optional(),
                    closeTime: z
                      .string()
                      .describe(
                        'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                      )
                      .optional(),
                  })
                )
                .optional(),
              specialHourPeriods: z
                .array(
                  z.object({
                    startDate: z
                      .string()
                      .describe(
                        'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                      )
                      .optional(),
                    endDate: z
                      .string()
                      .describe(
                        'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                      )
                      .optional(),
                    available: z
                      .boolean()
                      .describe(
                        'Whether the item is available during the exception. Defaults to `true`.'
                      )
                      .optional(),
                    eventName: z
                      .string()
                      .describe(
                        'Name of the special hour period. In the dashboard, the special hour period is called event.'
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .optional(),
            })
            .describe('Time periods when the entity is available.')
            .optional(),
        })
        .describe(
          'Visibility criteria that must be met for the menu to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
        )
        .optional(),
      sectionIds: z
        .object({ values: z.array(z.string()).optional() })
        .describe('IDs of the sections that are included in the menu.')
        .optional(),
      archived: z
        .boolean()
        .describe(
          "Whether the menu is archived. Defaults to `false`. **Note:** Archived menus can't be updated."
        )
        .optional()
        .nullable(),
    })
    .describe('Updated Menu.')
    .optional(),
});
export const UpdateSectionRequest = z.object({
  identifiers: z.object({
    catalogId: z
      .string()
      .describe('ID of the catalog the section belongs to.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
    menuId: z.string().describe('Menu ID.'),
    sectionId: z.string().describe('Section ID.'),
  }),
  options: z
    .object({
      section: z
        .object({
          _id: z.string().describe('Section ID.').optional().nullable(),
          menuId: z
            .string()
            .describe('ID of the menu the section belongs to.')
            .optional()
            .nullable(),
          name: z.string().describe('Section name.').optional().nullable(),
          description: z
            .string()
            .describe('Section description.')
            .optional()
            .nullable(),
          imageUrl: z
            .string()
            .describe("URL of the section's image file.")
            .optional()
            .nullable(),
          itemIds: z
            .object({ values: z.array(z.string()).optional() })
            .describe('IDs of the items that belong to the section.')
            .optional(),
          visibilityCriteria: z
            .object({
              visible: z
                .boolean()
                .describe(
                  "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
                )
                .optional()
                .nullable(),
              fulfillmentTypes: z
                .array(
                  z.enum([
                    'UNSPECIFIED_FULFILLMENT_TYPE',
                    'DELIVERY',
                    'PICKUP_OR_DINE_IN',
                  ])
                )
                .min(1)
                .max(2)
                .optional(),
              platforms: z
                .array(
                  z.enum([
                    'UNSPECIFIED_PLATFORM',
                    'SITE',
                    'MOBILE_SITE',
                    'WIX_APP',
                    'CALL_CENTER',
                    'CHAT_BOT',
                  ])
                )
                .min(1)
                .max(5)
                .optional(),
              availability: z
                .object({
                  periods: z
                    .array(
                      z.object({
                        openDay: z
                          .enum([
                            'UNDEFINED',
                            'SUN',
                            'MON',
                            'TUE',
                            'WED',
                            'THU',
                            'FRI',
                            'SAT',
                          ])
                          .optional(),
                        openTime: z
                          .string()
                          .describe(
                            'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                          )
                          .optional(),
                        closeDay: z
                          .enum([
                            'UNDEFINED',
                            'SUN',
                            'MON',
                            'TUE',
                            'WED',
                            'THU',
                            'FRI',
                            'SAT',
                          ])
                          .optional(),
                        closeTime: z
                          .string()
                          .describe(
                            'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                          )
                          .optional(),
                      })
                    )
                    .optional(),
                  specialHourPeriods: z
                    .array(
                      z.object({
                        startDate: z
                          .string()
                          .describe(
                            'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                          )
                          .optional(),
                        endDate: z
                          .string()
                          .describe(
                            'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                          )
                          .optional(),
                        available: z
                          .boolean()
                          .describe(
                            'Whether the item is available during the exception. Defaults to `true`.'
                          )
                          .optional(),
                        eventName: z
                          .string()
                          .describe(
                            'Name of the special hour period. In the dashboard, the special hour period is called event.'
                          )
                          .optional()
                          .nullable(),
                      })
                    )
                    .optional(),
                })
                .describe('Time periods when the entity is available.')
                .optional(),
            })
            .describe(
              'Visibility criteria that must be met for the section to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
            )
            .optional(),
          archived: z
            .boolean()
            .describe(
              "Whether the section is archived. Defaults to `false`. **Note:** Archived sections can't be updated."
            )
            .optional()
            .nullable(),
        })
        .describe('Section to update.')
        .optional(),
    })
    .optional(),
});
export const UpdateSectionResponse = z.object({
  section: z
    .object({
      _id: z.string().describe('Section ID.').optional().nullable(),
      menuId: z
        .string()
        .describe('ID of the menu the section belongs to.')
        .optional()
        .nullable(),
      name: z.string().describe('Section name.').optional().nullable(),
      description: z
        .string()
        .describe('Section description.')
        .optional()
        .nullable(),
      imageUrl: z
        .string()
        .describe("URL of the section's image file.")
        .optional()
        .nullable(),
      itemIds: z
        .object({ values: z.array(z.string()).optional() })
        .describe('IDs of the items that belong to the section.')
        .optional(),
      visibilityCriteria: z
        .object({
          visible: z
            .boolean()
            .describe(
              "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
            )
            .optional()
            .nullable(),
          fulfillmentTypes: z
            .array(
              z.enum([
                'UNSPECIFIED_FULFILLMENT_TYPE',
                'DELIVERY',
                'PICKUP_OR_DINE_IN',
              ])
            )
            .min(1)
            .max(2)
            .optional(),
          platforms: z
            .array(
              z.enum([
                'UNSPECIFIED_PLATFORM',
                'SITE',
                'MOBILE_SITE',
                'WIX_APP',
                'CALL_CENTER',
                'CHAT_BOT',
              ])
            )
            .min(1)
            .max(5)
            .optional(),
          availability: z
            .object({
              periods: z
                .array(
                  z.object({
                    openDay: z
                      .enum([
                        'UNDEFINED',
                        'SUN',
                        'MON',
                        'TUE',
                        'WED',
                        'THU',
                        'FRI',
                        'SAT',
                      ])
                      .describe('Day of the week the period starts on.')
                      .optional(),
                    openTime: z
                      .string()
                      .describe(
                        'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                      )
                      .optional(),
                    closeDay: z
                      .enum([
                        'UNDEFINED',
                        'SUN',
                        'MON',
                        'TUE',
                        'WED',
                        'THU',
                        'FRI',
                        'SAT',
                      ])
                      .describe('Day of the week the period ends on.')
                      .optional(),
                    closeTime: z
                      .string()
                      .describe(
                        'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                      )
                      .optional(),
                  })
                )
                .optional(),
              specialHourPeriods: z
                .array(
                  z.object({
                    startDate: z
                      .string()
                      .describe(
                        'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                      )
                      .optional(),
                    endDate: z
                      .string()
                      .describe(
                        'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                      )
                      .optional(),
                    available: z
                      .boolean()
                      .describe(
                        'Whether the item is available during the exception. Defaults to `true`.'
                      )
                      .optional(),
                    eventName: z
                      .string()
                      .describe(
                        'Name of the special hour period. In the dashboard, the special hour period is called event.'
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .optional(),
            })
            .describe('Time periods when the entity is available.')
            .optional(),
        })
        .describe(
          'Visibility criteria that must be met for the section to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
        )
        .optional(),
      archived: z
        .boolean()
        .describe(
          "Whether the section is archived. Defaults to `false`. **Note:** Archived sections can't be updated."
        )
        .optional()
        .nullable(),
    })
    .describe('Updated section.')
    .optional(),
});
export const ListMenusRequest = z.object({
  catalogId: z
    .string()
    .describe('ID of the catalog the menus belong to.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      fieldMask: z
        .object({ paths: z.array(z.string()) })
        .describe('Field mask path.')
        .optional()
        .nullable(),
      archived: z
        .boolean()
        .describe('Whether archived menus are returned. Defaults to `false`.')
        .optional(),
    })
    .optional(),
});
export const ListMenusResponse = z.object({
  menus: z
    .array(
      z.object({
        _id: z.string().describe('Menu ID.').optional().nullable(),
        name: z.string().describe('Menu name.').optional().nullable(),
        description: z
          .string()
          .describe('Menu description.')
          .optional()
          .nullable(),
        imageUrl: z
          .string()
          .describe("URL of the menu's image file.")
          .optional()
          .nullable(),
        visibilityCriteria: z
          .object({
            visible: z
              .boolean()
              .describe(
                "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
              )
              .optional()
              .nullable(),
            fulfillmentTypes: z
              .array(
                z.enum([
                  'UNSPECIFIED_FULFILLMENT_TYPE',
                  'DELIVERY',
                  'PICKUP_OR_DINE_IN',
                ])
              )
              .min(1)
              .max(2)
              .optional(),
            platforms: z
              .array(
                z.enum([
                  'UNSPECIFIED_PLATFORM',
                  'SITE',
                  'MOBILE_SITE',
                  'WIX_APP',
                  'CALL_CENTER',
                  'CHAT_BOT',
                ])
              )
              .min(1)
              .max(5)
              .optional(),
            availability: z
              .object({
                periods: z
                  .array(
                    z.object({
                      openDay: z
                        .enum([
                          'UNDEFINED',
                          'SUN',
                          'MON',
                          'TUE',
                          'WED',
                          'THU',
                          'FRI',
                          'SAT',
                        ])
                        .describe('Day of the week the period starts on.')
                        .optional(),
                      openTime: z
                        .string()
                        .describe(
                          'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                        )
                        .optional(),
                      closeDay: z
                        .enum([
                          'UNDEFINED',
                          'SUN',
                          'MON',
                          'TUE',
                          'WED',
                          'THU',
                          'FRI',
                          'SAT',
                        ])
                        .describe('Day of the week the period ends on.')
                        .optional(),
                      closeTime: z
                        .string()
                        .describe(
                          'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                        )
                        .optional(),
                    })
                  )
                  .optional(),
                specialHourPeriods: z
                  .array(
                    z.object({
                      startDate: z
                        .string()
                        .describe(
                          'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                        )
                        .optional(),
                      endDate: z
                        .string()
                        .describe(
                          'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                        )
                        .optional(),
                      available: z
                        .boolean()
                        .describe(
                          'Whether the item is available during the exception. Defaults to `true`.'
                        )
                        .optional(),
                      eventName: z
                        .string()
                        .describe(
                          'Name of the special hour period. In the dashboard, the special hour period is called event.'
                        )
                        .optional()
                        .nullable(),
                    })
                  )
                  .optional(),
              })
              .describe('Time periods when the entity is available.')
              .optional(),
          })
          .describe(
            'Visibility criteria that must be met for the menu to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
          )
          .optional(),
        sectionIds: z
          .object({ values: z.array(z.string()).optional() })
          .describe('IDs of the sections that are included in the menu.')
          .optional(),
        archived: z
          .boolean()
          .describe(
            "Whether the menu is archived. Defaults to `false`. **Note:** Archived menus can't be updated."
          )
          .optional()
          .nullable(),
      })
    )
    .optional(),
});
export const ListSectionsRequest = z.object({
  catalogId: z
    .string()
    .describe('ID of the catalog the sections belong to.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      fieldMask: z
        .object({ paths: z.array(z.string()) })
        .describe('Field mask path.')
        .optional()
        .nullable(),
      archived: z
        .boolean()
        .describe(
          'Whether archived sections are returned. Defaults to `false`.'
        )
        .optional(),
    })
    .optional(),
});
export const ListSectionsResponse = z.object({
  sections: z
    .array(
      z.object({
        _id: z.string().describe('Section ID.').optional().nullable(),
        menuId: z
          .string()
          .describe('ID of the menu the section belongs to.')
          .optional()
          .nullable(),
        name: z.string().describe('Section name.').optional().nullable(),
        description: z
          .string()
          .describe('Section description.')
          .optional()
          .nullable(),
        imageUrl: z
          .string()
          .describe("URL of the section's image file.")
          .optional()
          .nullable(),
        itemIds: z
          .object({ values: z.array(z.string()).optional() })
          .describe('IDs of the items that belong to the section.')
          .optional(),
        visibilityCriteria: z
          .object({
            visible: z
              .boolean()
              .describe(
                "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
              )
              .optional()
              .nullable(),
            fulfillmentTypes: z
              .array(
                z.enum([
                  'UNSPECIFIED_FULFILLMENT_TYPE',
                  'DELIVERY',
                  'PICKUP_OR_DINE_IN',
                ])
              )
              .min(1)
              .max(2)
              .optional(),
            platforms: z
              .array(
                z.enum([
                  'UNSPECIFIED_PLATFORM',
                  'SITE',
                  'MOBILE_SITE',
                  'WIX_APP',
                  'CALL_CENTER',
                  'CHAT_BOT',
                ])
              )
              .min(1)
              .max(5)
              .optional(),
            availability: z
              .object({
                periods: z
                  .array(
                    z.object({
                      openDay: z
                        .enum([
                          'UNDEFINED',
                          'SUN',
                          'MON',
                          'TUE',
                          'WED',
                          'THU',
                          'FRI',
                          'SAT',
                        ])
                        .describe('Day of the week the period starts on.')
                        .optional(),
                      openTime: z
                        .string()
                        .describe(
                          'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                        )
                        .optional(),
                      closeDay: z
                        .enum([
                          'UNDEFINED',
                          'SUN',
                          'MON',
                          'TUE',
                          'WED',
                          'THU',
                          'FRI',
                          'SAT',
                        ])
                        .describe('Day of the week the period ends on.')
                        .optional(),
                      closeTime: z
                        .string()
                        .describe(
                          'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                        )
                        .optional(),
                    })
                  )
                  .optional(),
                specialHourPeriods: z
                  .array(
                    z.object({
                      startDate: z
                        .string()
                        .describe(
                          'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                        )
                        .optional(),
                      endDate: z
                        .string()
                        .describe(
                          'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                        )
                        .optional(),
                      available: z
                        .boolean()
                        .describe(
                          'Whether the item is available during the exception. Defaults to `true`.'
                        )
                        .optional(),
                      eventName: z
                        .string()
                        .describe(
                          'Name of the special hour period. In the dashboard, the special hour period is called event.'
                        )
                        .optional()
                        .nullable(),
                    })
                  )
                  .optional(),
              })
              .describe('Time periods when the entity is available.')
              .optional(),
          })
          .describe(
            'Visibility criteria that must be met for the section to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
          )
          .optional(),
        archived: z
          .boolean()
          .describe(
            "Whether the section is archived. Defaults to `false`. **Note:** Archived sections can't be updated."
          )
          .optional()
          .nullable(),
      })
    )
    .optional(),
});
export const CreateMenuRequest = z.object({
  catalogId: z
    .string()
    .describe('ID of the catalog the menu will belong to.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  menu: z
    .object({
      _id: z.string().describe('Menu ID.').optional().nullable(),
      name: z.string().describe('Menu name.').optional().nullable(),
      description: z
        .string()
        .describe('Menu description.')
        .optional()
        .nullable(),
      imageUrl: z
        .string()
        .describe("URL of the menu's image file.")
        .optional()
        .nullable(),
      visibilityCriteria: z
        .object({
          visible: z
            .boolean()
            .describe(
              "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
            )
            .optional()
            .nullable(),
          fulfillmentTypes: z
            .array(
              z.enum([
                'UNSPECIFIED_FULFILLMENT_TYPE',
                'DELIVERY',
                'PICKUP_OR_DINE_IN',
              ])
            )
            .min(1)
            .max(2)
            .optional(),
          platforms: z
            .array(
              z.enum([
                'UNSPECIFIED_PLATFORM',
                'SITE',
                'MOBILE_SITE',
                'WIX_APP',
                'CALL_CENTER',
                'CHAT_BOT',
              ])
            )
            .min(1)
            .max(5)
            .optional(),
          availability: z
            .object({
              periods: z
                .array(
                  z.object({
                    openDay: z
                      .enum([
                        'UNDEFINED',
                        'SUN',
                        'MON',
                        'TUE',
                        'WED',
                        'THU',
                        'FRI',
                        'SAT',
                      ])
                      .optional(),
                    openTime: z
                      .string()
                      .describe(
                        'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                      )
                      .optional(),
                    closeDay: z
                      .enum([
                        'UNDEFINED',
                        'SUN',
                        'MON',
                        'TUE',
                        'WED',
                        'THU',
                        'FRI',
                        'SAT',
                      ])
                      .optional(),
                    closeTime: z
                      .string()
                      .describe(
                        'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                      )
                      .optional(),
                  })
                )
                .optional(),
              specialHourPeriods: z
                .array(
                  z.object({
                    startDate: z
                      .string()
                      .describe(
                        'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                      )
                      .optional(),
                    endDate: z
                      .string()
                      .describe(
                        'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                      )
                      .optional(),
                    available: z
                      .boolean()
                      .describe(
                        'Whether the item is available during the exception. Defaults to `true`.'
                      )
                      .optional(),
                    eventName: z
                      .string()
                      .describe(
                        'Name of the special hour period. In the dashboard, the special hour period is called event.'
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .optional(),
            })
            .describe('Time periods when the entity is available.')
            .optional(),
        })
        .describe(
          'Visibility criteria that must be met for the menu to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
        )
        .optional(),
      sectionIds: z
        .object({ values: z.array(z.string()).optional() })
        .describe('IDs of the sections that are included in the menu.')
        .optional(),
      archived: z
        .boolean()
        .describe(
          "Whether the menu is archived. Defaults to `false`. **Note:** Archived menus can't be updated."
        )
        .optional()
        .nullable(),
    })
    .describe('Menu to create.'),
});
export const CreateMenuResponse = z.object({
  menu: z
    .object({
      _id: z.string().describe('Menu ID.').optional().nullable(),
      name: z.string().describe('Menu name.').optional().nullable(),
      description: z
        .string()
        .describe('Menu description.')
        .optional()
        .nullable(),
      imageUrl: z
        .string()
        .describe("URL of the menu's image file.")
        .optional()
        .nullable(),
      visibilityCriteria: z
        .object({
          visible: z
            .boolean()
            .describe(
              "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
            )
            .optional()
            .nullable(),
          fulfillmentTypes: z
            .array(
              z.enum([
                'UNSPECIFIED_FULFILLMENT_TYPE',
                'DELIVERY',
                'PICKUP_OR_DINE_IN',
              ])
            )
            .min(1)
            .max(2)
            .optional(),
          platforms: z
            .array(
              z.enum([
                'UNSPECIFIED_PLATFORM',
                'SITE',
                'MOBILE_SITE',
                'WIX_APP',
                'CALL_CENTER',
                'CHAT_BOT',
              ])
            )
            .min(1)
            .max(5)
            .optional(),
          availability: z
            .object({
              periods: z
                .array(
                  z.object({
                    openDay: z
                      .enum([
                        'UNDEFINED',
                        'SUN',
                        'MON',
                        'TUE',
                        'WED',
                        'THU',
                        'FRI',
                        'SAT',
                      ])
                      .describe('Day of the week the period starts on.')
                      .optional(),
                    openTime: z
                      .string()
                      .describe(
                        'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                      )
                      .optional(),
                    closeDay: z
                      .enum([
                        'UNDEFINED',
                        'SUN',
                        'MON',
                        'TUE',
                        'WED',
                        'THU',
                        'FRI',
                        'SAT',
                      ])
                      .describe('Day of the week the period ends on.')
                      .optional(),
                    closeTime: z
                      .string()
                      .describe(
                        'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                      )
                      .optional(),
                  })
                )
                .optional(),
              specialHourPeriods: z
                .array(
                  z.object({
                    startDate: z
                      .string()
                      .describe(
                        'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                      )
                      .optional(),
                    endDate: z
                      .string()
                      .describe(
                        'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                      )
                      .optional(),
                    available: z
                      .boolean()
                      .describe(
                        'Whether the item is available during the exception. Defaults to `true`.'
                      )
                      .optional(),
                    eventName: z
                      .string()
                      .describe(
                        'Name of the special hour period. In the dashboard, the special hour period is called event.'
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .optional(),
            })
            .describe('Time periods when the entity is available.')
            .optional(),
        })
        .describe(
          'Visibility criteria that must be met for the menu to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
        )
        .optional(),
      sectionIds: z
        .object({ values: z.array(z.string()).optional() })
        .describe('IDs of the sections that are included in the menu.')
        .optional(),
      archived: z
        .boolean()
        .describe(
          "Whether the menu is archived. Defaults to `false`. **Note:** Archived menus can't be updated."
        )
        .optional()
        .nullable(),
    })
    .describe('Created Menu.')
    .optional(),
});
export const ArchiveMenuRequest = z.object({
  identifiers: z.object({
    catalogId: z
      .string()
      .describe('ID of the catalog the menu belongs to.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
    menuId: z.string().describe('Menu ID.'),
  }),
});
export const ArchiveMenuResponse = z.object({
  menu: z
    .object({
      _id: z.string().describe('Menu ID.').optional().nullable(),
      name: z.string().describe('Menu name.').optional().nullable(),
      description: z
        .string()
        .describe('Menu description.')
        .optional()
        .nullable(),
      imageUrl: z
        .string()
        .describe("URL of the menu's image file.")
        .optional()
        .nullable(),
      visibilityCriteria: z
        .object({
          visible: z
            .boolean()
            .describe(
              "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
            )
            .optional()
            .nullable(),
          fulfillmentTypes: z
            .array(
              z.enum([
                'UNSPECIFIED_FULFILLMENT_TYPE',
                'DELIVERY',
                'PICKUP_OR_DINE_IN',
              ])
            )
            .min(1)
            .max(2)
            .optional(),
          platforms: z
            .array(
              z.enum([
                'UNSPECIFIED_PLATFORM',
                'SITE',
                'MOBILE_SITE',
                'WIX_APP',
                'CALL_CENTER',
                'CHAT_BOT',
              ])
            )
            .min(1)
            .max(5)
            .optional(),
          availability: z
            .object({
              periods: z
                .array(
                  z.object({
                    openDay: z
                      .enum([
                        'UNDEFINED',
                        'SUN',
                        'MON',
                        'TUE',
                        'WED',
                        'THU',
                        'FRI',
                        'SAT',
                      ])
                      .describe('Day of the week the period starts on.')
                      .optional(),
                    openTime: z
                      .string()
                      .describe(
                        'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                      )
                      .optional(),
                    closeDay: z
                      .enum([
                        'UNDEFINED',
                        'SUN',
                        'MON',
                        'TUE',
                        'WED',
                        'THU',
                        'FRI',
                        'SAT',
                      ])
                      .describe('Day of the week the period ends on.')
                      .optional(),
                    closeTime: z
                      .string()
                      .describe(
                        'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                      )
                      .optional(),
                  })
                )
                .optional(),
              specialHourPeriods: z
                .array(
                  z.object({
                    startDate: z
                      .string()
                      .describe(
                        'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                      )
                      .optional(),
                    endDate: z
                      .string()
                      .describe(
                        'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                      )
                      .optional(),
                    available: z
                      .boolean()
                      .describe(
                        'Whether the item is available during the exception. Defaults to `true`.'
                      )
                      .optional(),
                    eventName: z
                      .string()
                      .describe(
                        'Name of the special hour period. In the dashboard, the special hour period is called event.'
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .optional(),
            })
            .describe('Time periods when the entity is available.')
            .optional(),
        })
        .describe(
          'Visibility criteria that must be met for the menu to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
        )
        .optional(),
      sectionIds: z
        .object({ values: z.array(z.string()).optional() })
        .describe('IDs of the sections that are included in the menu.')
        .optional(),
      archived: z
        .boolean()
        .describe(
          "Whether the menu is archived. Defaults to `false`. **Note:** Archived menus can't be updated."
        )
        .optional()
        .nullable(),
    })
    .describe('Archived menu.')
    .optional(),
});
export const UnarchiveMenuRequest = z.object({
  identifiers: z.object({
    catalogId: z
      .string()
      .describe('ID of the catalog the menu belongs to.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
    menuId: z.string().describe('Menu ID.'),
  }),
});
export const UnarchiveMenuResponse = z.object({
  menu: z
    .object({
      _id: z.string().describe('Menu ID.').optional().nullable(),
      name: z.string().describe('Menu name.').optional().nullable(),
      description: z
        .string()
        .describe('Menu description.')
        .optional()
        .nullable(),
      imageUrl: z
        .string()
        .describe("URL of the menu's image file.")
        .optional()
        .nullable(),
      visibilityCriteria: z
        .object({
          visible: z
            .boolean()
            .describe(
              "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
            )
            .optional()
            .nullable(),
          fulfillmentTypes: z
            .array(
              z.enum([
                'UNSPECIFIED_FULFILLMENT_TYPE',
                'DELIVERY',
                'PICKUP_OR_DINE_IN',
              ])
            )
            .min(1)
            .max(2)
            .optional(),
          platforms: z
            .array(
              z.enum([
                'UNSPECIFIED_PLATFORM',
                'SITE',
                'MOBILE_SITE',
                'WIX_APP',
                'CALL_CENTER',
                'CHAT_BOT',
              ])
            )
            .min(1)
            .max(5)
            .optional(),
          availability: z
            .object({
              periods: z
                .array(
                  z.object({
                    openDay: z
                      .enum([
                        'UNDEFINED',
                        'SUN',
                        'MON',
                        'TUE',
                        'WED',
                        'THU',
                        'FRI',
                        'SAT',
                      ])
                      .describe('Day of the week the period starts on.')
                      .optional(),
                    openTime: z
                      .string()
                      .describe(
                        'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                      )
                      .optional(),
                    closeDay: z
                      .enum([
                        'UNDEFINED',
                        'SUN',
                        'MON',
                        'TUE',
                        'WED',
                        'THU',
                        'FRI',
                        'SAT',
                      ])
                      .describe('Day of the week the period ends on.')
                      .optional(),
                    closeTime: z
                      .string()
                      .describe(
                        'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                      )
                      .optional(),
                  })
                )
                .optional(),
              specialHourPeriods: z
                .array(
                  z.object({
                    startDate: z
                      .string()
                      .describe(
                        'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                      )
                      .optional(),
                    endDate: z
                      .string()
                      .describe(
                        'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                      )
                      .optional(),
                    available: z
                      .boolean()
                      .describe(
                        'Whether the item is available during the exception. Defaults to `true`.'
                      )
                      .optional(),
                    eventName: z
                      .string()
                      .describe(
                        'Name of the special hour period. In the dashboard, the special hour period is called event.'
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .optional(),
            })
            .describe('Time periods when the entity is available.')
            .optional(),
        })
        .describe(
          'Visibility criteria that must be met for the menu to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
        )
        .optional(),
      sectionIds: z
        .object({ values: z.array(z.string()).optional() })
        .describe('IDs of the sections that are included in the menu.')
        .optional(),
      archived: z
        .boolean()
        .describe(
          "Whether the menu is archived. Defaults to `false`. **Note:** Archived menus can't be updated."
        )
        .optional()
        .nullable(),
    })
    .describe('Unarchived menu.')
    .optional(),
});
export const CreateSectionRequest = z.object({
  identifiers: z.object({
    catalogId: z
      .string()
      .describe('ID of the catalog the section will belong to.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
    menuId: z.string().describe('ID of the menu the section will belong to.'),
  }),
  section: z
    .object({
      _id: z.string().describe('Section ID.').optional().nullable(),
      menuId: z
        .string()
        .describe('ID of the menu the section belongs to.')
        .optional()
        .nullable(),
      name: z.string().describe('Section name.').optional().nullable(),
      description: z
        .string()
        .describe('Section description.')
        .optional()
        .nullable(),
      imageUrl: z
        .string()
        .describe("URL of the section's image file.")
        .optional()
        .nullable(),
      itemIds: z
        .object({ values: z.array(z.string()).optional() })
        .describe('IDs of the items that belong to the section.')
        .optional(),
      visibilityCriteria: z
        .object({
          visible: z
            .boolean()
            .describe(
              "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
            )
            .optional()
            .nullable(),
          fulfillmentTypes: z
            .array(
              z.enum([
                'UNSPECIFIED_FULFILLMENT_TYPE',
                'DELIVERY',
                'PICKUP_OR_DINE_IN',
              ])
            )
            .min(1)
            .max(2)
            .optional(),
          platforms: z
            .array(
              z.enum([
                'UNSPECIFIED_PLATFORM',
                'SITE',
                'MOBILE_SITE',
                'WIX_APP',
                'CALL_CENTER',
                'CHAT_BOT',
              ])
            )
            .min(1)
            .max(5)
            .optional(),
          availability: z
            .object({
              periods: z
                .array(
                  z.object({
                    openDay: z
                      .enum([
                        'UNDEFINED',
                        'SUN',
                        'MON',
                        'TUE',
                        'WED',
                        'THU',
                        'FRI',
                        'SAT',
                      ])
                      .optional(),
                    openTime: z
                      .string()
                      .describe(
                        'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                      )
                      .optional(),
                    closeDay: z
                      .enum([
                        'UNDEFINED',
                        'SUN',
                        'MON',
                        'TUE',
                        'WED',
                        'THU',
                        'FRI',
                        'SAT',
                      ])
                      .optional(),
                    closeTime: z
                      .string()
                      .describe(
                        'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                      )
                      .optional(),
                  })
                )
                .optional(),
              specialHourPeriods: z
                .array(
                  z.object({
                    startDate: z
                      .string()
                      .describe(
                        'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                      )
                      .optional(),
                    endDate: z
                      .string()
                      .describe(
                        'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                      )
                      .optional(),
                    available: z
                      .boolean()
                      .describe(
                        'Whether the item is available during the exception. Defaults to `true`.'
                      )
                      .optional(),
                    eventName: z
                      .string()
                      .describe(
                        'Name of the special hour period. In the dashboard, the special hour period is called event.'
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .optional(),
            })
            .describe('Time periods when the entity is available.')
            .optional(),
        })
        .describe(
          'Visibility criteria that must be met for the section to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
        )
        .optional(),
      archived: z
        .boolean()
        .describe(
          "Whether the section is archived. Defaults to `false`. **Note:** Archived sections can't be updated."
        )
        .optional()
        .nullable(),
    })
    .describe('Section to create.'),
});
export const CreateSectionResponse = z.object({
  section: z
    .object({
      _id: z.string().describe('Section ID.').optional().nullable(),
      menuId: z
        .string()
        .describe('ID of the menu the section belongs to.')
        .optional()
        .nullable(),
      name: z.string().describe('Section name.').optional().nullable(),
      description: z
        .string()
        .describe('Section description.')
        .optional()
        .nullable(),
      imageUrl: z
        .string()
        .describe("URL of the section's image file.")
        .optional()
        .nullable(),
      itemIds: z
        .object({ values: z.array(z.string()).optional() })
        .describe('IDs of the items that belong to the section.')
        .optional(),
      visibilityCriteria: z
        .object({
          visible: z
            .boolean()
            .describe(
              "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
            )
            .optional()
            .nullable(),
          fulfillmentTypes: z
            .array(
              z.enum([
                'UNSPECIFIED_FULFILLMENT_TYPE',
                'DELIVERY',
                'PICKUP_OR_DINE_IN',
              ])
            )
            .min(1)
            .max(2)
            .optional(),
          platforms: z
            .array(
              z.enum([
                'UNSPECIFIED_PLATFORM',
                'SITE',
                'MOBILE_SITE',
                'WIX_APP',
                'CALL_CENTER',
                'CHAT_BOT',
              ])
            )
            .min(1)
            .max(5)
            .optional(),
          availability: z
            .object({
              periods: z
                .array(
                  z.object({
                    openDay: z
                      .enum([
                        'UNDEFINED',
                        'SUN',
                        'MON',
                        'TUE',
                        'WED',
                        'THU',
                        'FRI',
                        'SAT',
                      ])
                      .describe('Day of the week the period starts on.')
                      .optional(),
                    openTime: z
                      .string()
                      .describe(
                        'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                      )
                      .optional(),
                    closeDay: z
                      .enum([
                        'UNDEFINED',
                        'SUN',
                        'MON',
                        'TUE',
                        'WED',
                        'THU',
                        'FRI',
                        'SAT',
                      ])
                      .describe('Day of the week the period ends on.')
                      .optional(),
                    closeTime: z
                      .string()
                      .describe(
                        'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                      )
                      .optional(),
                  })
                )
                .optional(),
              specialHourPeriods: z
                .array(
                  z.object({
                    startDate: z
                      .string()
                      .describe(
                        'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                      )
                      .optional(),
                    endDate: z
                      .string()
                      .describe(
                        'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                      )
                      .optional(),
                    available: z
                      .boolean()
                      .describe(
                        'Whether the item is available during the exception. Defaults to `true`.'
                      )
                      .optional(),
                    eventName: z
                      .string()
                      .describe(
                        'Name of the special hour period. In the dashboard, the special hour period is called event.'
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .optional(),
            })
            .describe('Time periods when the entity is available.')
            .optional(),
        })
        .describe(
          'Visibility criteria that must be met for the section to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
        )
        .optional(),
      archived: z
        .boolean()
        .describe(
          "Whether the section is archived. Defaults to `false`. **Note:** Archived sections can't be updated."
        )
        .optional()
        .nullable(),
    })
    .describe('Created section.')
    .optional(),
});
export const GetItemRequest = z.object({
  identifiers: z.object({
    catalogId: z
      .string()
      .describe('ID of the catalog the item belongs to.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
    itemId: z.string().describe('Item ID.'),
  }),
  options: z
    .object({
      includeVisibilityCriteria: z
        .boolean()
        .describe(
          'Whether `visibilityCriteria` is returned. Defaults to `false`.'
        )
        .optional(),
    })
    .optional(),
});
export const GetItemResponse = z.object({
  item: z
    .object({
      _id: z.string().describe('Item ID.').optional().nullable(),
      name: z.string().describe('Item name.').optional().nullable(),
      description: z
        .string()
        .describe('Item description.')
        .optional()
        .nullable(),
      price: z
        .object({
          value: z
            .string()
            .describe(
              'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
            )
            .optional(),
          currency: z
            .string()
            .describe(
              'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
            )
            .optional(),
        })
        .describe('Item price.')
        .optional(),
      imageUrl: z
        .string()
        .describe("URL of the item's image file.")
        .optional()
        .nullable(),
      labels: z
        .object({ values: z.array(z.string()).optional() })
        .describe(
          'Item labels. For example spicy, hot, vegan, gluten-free, or organic.'
        )
        .optional(),
      inStock: z
        .boolean()
        .describe('Whether the item is in stock.')
        .optional()
        .nullable(),
      taxRate: z
        .string()
        .describe('Tax rate of the item in percent.')
        .optional()
        .nullable(),
      archived: z
        .boolean()
        .describe(
          "Whether the item is archived. Defaults to `false`. **Note:** Archived items can't be updated."
        )
        .optional()
        .nullable(),
      visibilityCriteria: z
        .object({
          visible: z
            .boolean()
            .describe(
              "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
            )
            .optional()
            .nullable(),
          fulfillmentTypes: z
            .array(
              z.enum([
                'UNSPECIFIED_FULFILLMENT_TYPE',
                'DELIVERY',
                'PICKUP_OR_DINE_IN',
              ])
            )
            .min(1)
            .max(2)
            .optional(),
          platforms: z
            .array(
              z.enum([
                'UNSPECIFIED_PLATFORM',
                'SITE',
                'MOBILE_SITE',
                'WIX_APP',
                'CALL_CENTER',
                'CHAT_BOT',
              ])
            )
            .min(1)
            .max(5)
            .optional(),
          availability: z
            .object({
              periods: z
                .array(
                  z.object({
                    openDay: z
                      .enum([
                        'UNDEFINED',
                        'SUN',
                        'MON',
                        'TUE',
                        'WED',
                        'THU',
                        'FRI',
                        'SAT',
                      ])
                      .describe('Day of the week the period starts on.')
                      .optional(),
                    openTime: z
                      .string()
                      .describe(
                        'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                      )
                      .optional(),
                    closeDay: z
                      .enum([
                        'UNDEFINED',
                        'SUN',
                        'MON',
                        'TUE',
                        'WED',
                        'THU',
                        'FRI',
                        'SAT',
                      ])
                      .describe('Day of the week the period ends on.')
                      .optional(),
                    closeTime: z
                      .string()
                      .describe(
                        'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                      )
                      .optional(),
                  })
                )
                .optional(),
              specialHourPeriods: z
                .array(
                  z.object({
                    startDate: z
                      .string()
                      .describe(
                        'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                      )
                      .optional(),
                    endDate: z
                      .string()
                      .describe(
                        'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                      )
                      .optional(),
                    available: z
                      .boolean()
                      .describe(
                        'Whether the item is available during the exception. Defaults to `true`.'
                      )
                      .optional(),
                    eventName: z
                      .string()
                      .describe(
                        'Name of the special hour period. In the dashboard, the special hour period is called event.'
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .optional(),
            })
            .describe('Time periods when the entity is available.')
            .optional(),
        })
        .describe(
          'Visibility criteria that must be met for the item to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
        )
        .optional(),
      dishOptions: z
        .object({
          values: z
            .array(
              z.intersection(
                z.object({
                  name: z.string().describe('Dish option name.').optional(),
                  type: z
                    .enum([
                      'UNSPECIFIED_DISH_OPTION_TYPE',
                      'SELECTION',
                      'EXTRAS',
                      'DESELECTION',
                    ])
                    .optional(),
                }),
                z.xor([
                  z.object({
                    selection: z.never().optional(),
                    extras: z.never().optional(),
                    deselection: z.never().optional(),
                  }),
                  z.object({
                    extras: z.never().optional(),
                    deselection: z.never().optional(),
                    selection: z
                      .object({
                        defaultChoice: z
                          .string()
                          .describe('Item ID of the default choice.')
                          .optional()
                          .nullable(),
                        availableChoices: z
                          .array(
                            z.object({
                              itemId: z
                                .string()
                                .describe('Item ID of the dish option.')
                                .optional(),
                              price: z
                                .object({
                                  value: z
                                    .string()
                                    .describe(
                                      'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                    )
                                    .optional(),
                                  currency: z
                                    .string()
                                    .describe(
                                      'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  "Dish option price. `0` for free choices that are included in the dish's price."
                                )
                                .optional(),
                            })
                          )
                          .optional(),
                      })
                      .describe(
                        'Item of type `DISH` or `VARIATION` that customers can select. For example a dish size. Customers can choose only a single selection per dish.'
                      ),
                  }),
                  z.object({
                    selection: z.never().optional(),
                    deselection: z.never().optional(),
                    extras: z
                      .object({
                        defaultChoices: z.array(z.string()).optional(),
                        minChoices: z
                          .number()
                          .int()
                          .describe(
                            'Minimum number of extras a customer must choose. Defaults to `0`. Must be lower than or equal to the value of `availableChoices`.'
                          )
                          .min(0)
                          .max(2147483647)
                          .optional()
                          .nullable(),
                        maxChoices: z
                          .number()
                          .int()
                          .describe(
                            'Maximum number of extras a customer can choose. Defaults to the value of `availableChoices`. Must be greater than or equal to the value of `minChoices`.'
                          )
                          .min(0)
                          .max(2147483647)
                          .optional()
                          .nullable(),
                        availableChoices: z
                          .array(
                            z.object({
                              itemId: z
                                .string()
                                .describe('Item ID of the dish option.')
                                .optional(),
                              price: z
                                .object({
                                  value: z
                                    .string()
                                    .describe(
                                      'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                    )
                                    .optional(),
                                  currency: z
                                    .string()
                                    .describe(
                                      'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  "Dish option price. `0` for free choices that are included in the dish's price."
                                )
                                .optional(),
                            })
                          )
                          .optional(),
                      })
                      .describe(
                        'Item of type `DISH` or `VARIATION` that customers can add to a dish. For example a topping. Customers can add multiple extras per dish.'
                      ),
                  }),
                  z.object({
                    selection: z.never().optional(),
                    extras: z.never().optional(),
                    deselection: z
                      .object({
                        availableChoices: z
                          .array(
                            z.object({
                              itemId: z
                                .string()
                                .describe('Item ID of the dish option.')
                                .optional(),
                              price: z
                                .object({
                                  value: z
                                    .string()
                                    .describe(
                                      'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                    )
                                    .optional(),
                                  currency: z
                                    .string()
                                    .describe(
                                      'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  "Dish option price. `0` for free choices that are included in the dish's price."
                                )
                                .optional(),
                            })
                          )
                          .optional(),
                      })
                      .describe(
                        'Item of type `DISH` or `VARIATION` that customers can remove from a dish. For example a specific ingredient. Customers can remove multiple deselections per dish.'
                      ),
                  }),
                ])
              )
            )
            .optional(),
        })
        .describe(
          'Items customers can choose to modify a dish. Can be an extra, selection, or deselection.'
        )
        .optional(),
      acceptSpecialRequest: z
        .boolean()
        .describe(
          'Whether a customer can add a special request when ordering this item. Defaults to `true`.'
        )
        .optional()
        .nullable(),
      type: z
        .enum(['UNSPECIFIED_ITEM_TYPE', 'DISH', 'VARIATION'])
        .describe('Item type.')
        .optional(),
    })
    .describe('Retrieved item.')
    .optional(),
});
export const ListItemsRequest = z.object({
  catalogId: z
    .string()
    .describe('ID of the catalog the items belong to.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      fieldMask: z
        .object({ paths: z.array(z.string()) })
        .describe('Field mask path.')
        .optional()
        .nullable(),
      archived: z
        .boolean()
        .describe('Whether archived items are returned. Defaults to `false`.')
        .optional(),
    })
    .optional(),
});
export const ListItemsResponse = z.object({
  items: z
    .array(
      z.object({
        _id: z.string().describe('Item ID.').optional().nullable(),
        name: z.string().describe('Item name.').optional().nullable(),
        description: z
          .string()
          .describe('Item description.')
          .optional()
          .nullable(),
        price: z
          .object({
            value: z
              .string()
              .describe(
                'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
              )
              .optional(),
            currency: z
              .string()
              .describe(
                'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
              )
              .optional(),
          })
          .describe('Item price.')
          .optional(),
        imageUrl: z
          .string()
          .describe("URL of the item's image file.")
          .optional()
          .nullable(),
        labels: z
          .object({ values: z.array(z.string()).optional() })
          .describe(
            'Item labels. For example spicy, hot, vegan, gluten-free, or organic.'
          )
          .optional(),
        inStock: z
          .boolean()
          .describe('Whether the item is in stock.')
          .optional()
          .nullable(),
        taxRate: z
          .string()
          .describe('Tax rate of the item in percent.')
          .optional()
          .nullable(),
        archived: z
          .boolean()
          .describe(
            "Whether the item is archived. Defaults to `false`. **Note:** Archived items can't be updated."
          )
          .optional()
          .nullable(),
        visibilityCriteria: z
          .object({
            visible: z
              .boolean()
              .describe(
                "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
              )
              .optional()
              .nullable(),
            fulfillmentTypes: z
              .array(
                z.enum([
                  'UNSPECIFIED_FULFILLMENT_TYPE',
                  'DELIVERY',
                  'PICKUP_OR_DINE_IN',
                ])
              )
              .min(1)
              .max(2)
              .optional(),
            platforms: z
              .array(
                z.enum([
                  'UNSPECIFIED_PLATFORM',
                  'SITE',
                  'MOBILE_SITE',
                  'WIX_APP',
                  'CALL_CENTER',
                  'CHAT_BOT',
                ])
              )
              .min(1)
              .max(5)
              .optional(),
            availability: z
              .object({
                periods: z
                  .array(
                    z.object({
                      openDay: z
                        .enum([
                          'UNDEFINED',
                          'SUN',
                          'MON',
                          'TUE',
                          'WED',
                          'THU',
                          'FRI',
                          'SAT',
                        ])
                        .describe('Day of the week the period starts on.')
                        .optional(),
                      openTime: z
                        .string()
                        .describe(
                          'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                        )
                        .optional(),
                      closeDay: z
                        .enum([
                          'UNDEFINED',
                          'SUN',
                          'MON',
                          'TUE',
                          'WED',
                          'THU',
                          'FRI',
                          'SAT',
                        ])
                        .describe('Day of the week the period ends on.')
                        .optional(),
                      closeTime: z
                        .string()
                        .describe(
                          'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                        )
                        .optional(),
                    })
                  )
                  .optional(),
                specialHourPeriods: z
                  .array(
                    z.object({
                      startDate: z
                        .string()
                        .describe(
                          'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                        )
                        .optional(),
                      endDate: z
                        .string()
                        .describe(
                          'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                        )
                        .optional(),
                      available: z
                        .boolean()
                        .describe(
                          'Whether the item is available during the exception. Defaults to `true`.'
                        )
                        .optional(),
                      eventName: z
                        .string()
                        .describe(
                          'Name of the special hour period. In the dashboard, the special hour period is called event.'
                        )
                        .optional()
                        .nullable(),
                    })
                  )
                  .optional(),
              })
              .describe('Time periods when the entity is available.')
              .optional(),
          })
          .describe(
            'Visibility criteria that must be met for the item to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
          )
          .optional(),
        dishOptions: z
          .object({
            values: z
              .array(
                z.intersection(
                  z.object({
                    name: z.string().describe('Dish option name.').optional(),
                    type: z
                      .enum([
                        'UNSPECIFIED_DISH_OPTION_TYPE',
                        'SELECTION',
                        'EXTRAS',
                        'DESELECTION',
                      ])
                      .optional(),
                  }),
                  z.xor([
                    z.object({
                      selection: z.never().optional(),
                      extras: z.never().optional(),
                      deselection: z.never().optional(),
                    }),
                    z.object({
                      extras: z.never().optional(),
                      deselection: z.never().optional(),
                      selection: z
                        .object({
                          defaultChoice: z
                            .string()
                            .describe('Item ID of the default choice.')
                            .optional()
                            .nullable(),
                          availableChoices: z
                            .array(
                              z.object({
                                itemId: z
                                  .string()
                                  .describe('Item ID of the dish option.')
                                  .optional(),
                                price: z
                                  .object({
                                    value: z
                                      .string()
                                      .describe(
                                        'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                      )
                                      .optional(),
                                    currency: z
                                      .string()
                                      .describe(
                                        'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    "Dish option price. `0` for free choices that are included in the dish's price."
                                  )
                                  .optional(),
                              })
                            )
                            .optional(),
                        })
                        .describe(
                          'Item of type `DISH` or `VARIATION` that customers can select. For example a dish size. Customers can choose only a single selection per dish.'
                        ),
                    }),
                    z.object({
                      selection: z.never().optional(),
                      deselection: z.never().optional(),
                      extras: z
                        .object({
                          defaultChoices: z.array(z.string()).optional(),
                          minChoices: z
                            .number()
                            .int()
                            .describe(
                              'Minimum number of extras a customer must choose. Defaults to `0`. Must be lower than or equal to the value of `availableChoices`.'
                            )
                            .min(0)
                            .max(2147483647)
                            .optional()
                            .nullable(),
                          maxChoices: z
                            .number()
                            .int()
                            .describe(
                              'Maximum number of extras a customer can choose. Defaults to the value of `availableChoices`. Must be greater than or equal to the value of `minChoices`.'
                            )
                            .min(0)
                            .max(2147483647)
                            .optional()
                            .nullable(),
                          availableChoices: z
                            .array(
                              z.object({
                                itemId: z
                                  .string()
                                  .describe('Item ID of the dish option.')
                                  .optional(),
                                price: z
                                  .object({
                                    value: z
                                      .string()
                                      .describe(
                                        'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                      )
                                      .optional(),
                                    currency: z
                                      .string()
                                      .describe(
                                        'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    "Dish option price. `0` for free choices that are included in the dish's price."
                                  )
                                  .optional(),
                              })
                            )
                            .optional(),
                        })
                        .describe(
                          'Item of type `DISH` or `VARIATION` that customers can add to a dish. For example a topping. Customers can add multiple extras per dish.'
                        ),
                    }),
                    z.object({
                      selection: z.never().optional(),
                      extras: z.never().optional(),
                      deselection: z
                        .object({
                          availableChoices: z
                            .array(
                              z.object({
                                itemId: z
                                  .string()
                                  .describe('Item ID of the dish option.')
                                  .optional(),
                                price: z
                                  .object({
                                    value: z
                                      .string()
                                      .describe(
                                        'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                      )
                                      .optional(),
                                    currency: z
                                      .string()
                                      .describe(
                                        'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    "Dish option price. `0` for free choices that are included in the dish's price."
                                  )
                                  .optional(),
                              })
                            )
                            .optional(),
                        })
                        .describe(
                          'Item of type `DISH` or `VARIATION` that customers can remove from a dish. For example a specific ingredient. Customers can remove multiple deselections per dish.'
                        ),
                    }),
                  ])
                )
              )
              .optional(),
          })
          .describe(
            'Items customers can choose to modify a dish. Can be an extra, selection, or deselection.'
          )
          .optional(),
        acceptSpecialRequest: z
          .boolean()
          .describe(
            'Whether a customer can add a special request when ordering this item. Defaults to `true`.'
          )
          .optional()
          .nullable(),
        type: z
          .enum(['UNSPECIFIED_ITEM_TYPE', 'DISH', 'VARIATION'])
          .describe('Item type.')
          .optional(),
      })
    )
    .optional(),
});
export const UpdateItemRequest = z.object({
  identifiers: z.object({
    catalogId: z
      .string()
      .describe('ID of the catalog the item belongs to.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
    itemId: z.string().describe('Item ID.'),
  }),
  options: z
    .object({
      item: z
        .object({
          _id: z.string().describe('Item ID.').optional().nullable(),
          name: z.string().describe('Item name.').optional().nullable(),
          description: z
            .string()
            .describe('Item description.')
            .optional()
            .nullable(),
          price: z
            .object({
              value: z
                .string()
                .describe(
                  'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                )
                .optional(),
              currency: z
                .string()
                .describe(
                  'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                )
                .optional(),
            })
            .describe('Item price.')
            .optional(),
          imageUrl: z
            .string()
            .describe("URL of the item's image file.")
            .optional()
            .nullable(),
          labels: z
            .object({ values: z.array(z.string()).optional() })
            .describe(
              'Item labels. For example spicy, hot, vegan, gluten-free, or organic.'
            )
            .optional(),
          inStock: z
            .boolean()
            .describe('Whether the item is in stock.')
            .optional()
            .nullable(),
          taxRate: z
            .string()
            .describe('Tax rate of the item in percent.')
            .optional()
            .nullable(),
          archived: z
            .boolean()
            .describe(
              "Whether the item is archived. Defaults to `false`. **Note:** Archived items can't be updated."
            )
            .optional()
            .nullable(),
          visibilityCriteria: z
            .object({
              visible: z
                .boolean()
                .describe(
                  "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
                )
                .optional()
                .nullable(),
              fulfillmentTypes: z
                .array(
                  z.enum([
                    'UNSPECIFIED_FULFILLMENT_TYPE',
                    'DELIVERY',
                    'PICKUP_OR_DINE_IN',
                  ])
                )
                .min(1)
                .max(2)
                .optional(),
              platforms: z
                .array(
                  z.enum([
                    'UNSPECIFIED_PLATFORM',
                    'SITE',
                    'MOBILE_SITE',
                    'WIX_APP',
                    'CALL_CENTER',
                    'CHAT_BOT',
                  ])
                )
                .min(1)
                .max(5)
                .optional(),
              availability: z
                .object({
                  periods: z
                    .array(
                      z.object({
                        openDay: z
                          .enum([
                            'UNDEFINED',
                            'SUN',
                            'MON',
                            'TUE',
                            'WED',
                            'THU',
                            'FRI',
                            'SAT',
                          ])
                          .optional(),
                        openTime: z
                          .string()
                          .describe(
                            'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                          )
                          .optional(),
                        closeDay: z
                          .enum([
                            'UNDEFINED',
                            'SUN',
                            'MON',
                            'TUE',
                            'WED',
                            'THU',
                            'FRI',
                            'SAT',
                          ])
                          .optional(),
                        closeTime: z
                          .string()
                          .describe(
                            'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                          )
                          .optional(),
                      })
                    )
                    .optional(),
                  specialHourPeriods: z
                    .array(
                      z.object({
                        startDate: z
                          .string()
                          .describe(
                            'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                          )
                          .optional(),
                        endDate: z
                          .string()
                          .describe(
                            'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                          )
                          .optional(),
                        available: z
                          .boolean()
                          .describe(
                            'Whether the item is available during the exception. Defaults to `true`.'
                          )
                          .optional(),
                        eventName: z
                          .string()
                          .describe(
                            'Name of the special hour period. In the dashboard, the special hour period is called event.'
                          )
                          .optional()
                          .nullable(),
                      })
                    )
                    .optional(),
                })
                .describe('Time periods when the entity is available.')
                .optional(),
            })
            .describe(
              'Visibility criteria that must be met for the item to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
            )
            .optional(),
          dishOptions: z
            .object({
              values: z
                .array(
                  z.intersection(
                    z.object({
                      name: z.string().describe('Dish option name.').optional(),
                      type: z
                        .enum([
                          'UNSPECIFIED_DISH_OPTION_TYPE',
                          'SELECTION',
                          'EXTRAS',
                          'DESELECTION',
                        ])
                        .optional(),
                    }),
                    z.xor([
                      z.object({
                        selection: z.never().optional(),
                        extras: z.never().optional(),
                        deselection: z.never().optional(),
                      }),
                      z.object({
                        extras: z.never().optional(),
                        deselection: z.never().optional(),
                        selection: z
                          .object({
                            defaultChoice: z
                              .string()
                              .describe('Item ID of the default choice.')
                              .optional()
                              .nullable(),
                            availableChoices: z
                              .array(
                                z.object({
                                  itemId: z
                                    .string()
                                    .describe('Item ID of the dish option.')
                                    .optional(),
                                  price: z
                                    .object({
                                      value: z
                                        .string()
                                        .describe(
                                          'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                        )
                                        .optional(),
                                      currency: z
                                        .string()
                                        .describe(
                                          'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                        )
                                        .optional(),
                                    })
                                    .describe(
                                      "Dish option price. `0` for free choices that are included in the dish's price."
                                    )
                                    .optional(),
                                })
                              )
                              .optional(),
                          })
                          .describe(
                            'Item of type `DISH` or `VARIATION` that customers can select. For example a dish size. Customers can choose only a single selection per dish.'
                          ),
                      }),
                      z.object({
                        selection: z.never().optional(),
                        deselection: z.never().optional(),
                        extras: z
                          .object({
                            defaultChoices: z.array(z.string()).optional(),
                            minChoices: z
                              .number()
                              .int()
                              .describe(
                                'Minimum number of extras a customer must choose. Defaults to `0`. Must be lower than or equal to the value of `availableChoices`.'
                              )
                              .min(0)
                              .max(2147483647)
                              .optional()
                              .nullable(),
                            maxChoices: z
                              .number()
                              .int()
                              .describe(
                                'Maximum number of extras a customer can choose. Defaults to the value of `availableChoices`. Must be greater than or equal to the value of `minChoices`.'
                              )
                              .min(0)
                              .max(2147483647)
                              .optional()
                              .nullable(),
                            availableChoices: z
                              .array(
                                z.object({
                                  itemId: z
                                    .string()
                                    .describe('Item ID of the dish option.')
                                    .optional(),
                                  price: z
                                    .object({
                                      value: z
                                        .string()
                                        .describe(
                                          'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                        )
                                        .optional(),
                                      currency: z
                                        .string()
                                        .describe(
                                          'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                        )
                                        .optional(),
                                    })
                                    .describe(
                                      "Dish option price. `0` for free choices that are included in the dish's price."
                                    )
                                    .optional(),
                                })
                              )
                              .optional(),
                          })
                          .describe(
                            'Item of type `DISH` or `VARIATION` that customers can add to a dish. For example a topping. Customers can add multiple extras per dish.'
                          ),
                      }),
                      z.object({
                        selection: z.never().optional(),
                        extras: z.never().optional(),
                        deselection: z
                          .object({
                            availableChoices: z
                              .array(
                                z.object({
                                  itemId: z
                                    .string()
                                    .describe('Item ID of the dish option.')
                                    .optional(),
                                  price: z
                                    .object({
                                      value: z
                                        .string()
                                        .describe(
                                          'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                        )
                                        .optional(),
                                      currency: z
                                        .string()
                                        .describe(
                                          'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                        )
                                        .optional(),
                                    })
                                    .describe(
                                      "Dish option price. `0` for free choices that are included in the dish's price."
                                    )
                                    .optional(),
                                })
                              )
                              .optional(),
                          })
                          .describe(
                            'Item of type `DISH` or `VARIATION` that customers can remove from a dish. For example a specific ingredient. Customers can remove multiple deselections per dish.'
                          ),
                      }),
                    ])
                  )
                )
                .optional(),
            })
            .describe(
              'Items customers can choose to modify a dish. Can be an extra, selection, or deselection.'
            )
            .optional(),
          acceptSpecialRequest: z
            .boolean()
            .describe(
              'Whether a customer can add a special request when ordering this item. Defaults to `true`.'
            )
            .optional()
            .nullable(),
          type: z
            .enum(['UNSPECIFIED_ITEM_TYPE', 'DISH', 'VARIATION'])
            .optional(),
        })
        .describe('Item to update.')
        .optional(),
    })
    .optional(),
});
export const UpdateItemResponse = z.object({
  item: z
    .object({
      _id: z.string().describe('Item ID.').optional().nullable(),
      name: z.string().describe('Item name.').optional().nullable(),
      description: z
        .string()
        .describe('Item description.')
        .optional()
        .nullable(),
      price: z
        .object({
          value: z
            .string()
            .describe(
              'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
            )
            .optional(),
          currency: z
            .string()
            .describe(
              'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
            )
            .optional(),
        })
        .describe('Item price.')
        .optional(),
      imageUrl: z
        .string()
        .describe("URL of the item's image file.")
        .optional()
        .nullable(),
      labels: z
        .object({ values: z.array(z.string()).optional() })
        .describe(
          'Item labels. For example spicy, hot, vegan, gluten-free, or organic.'
        )
        .optional(),
      inStock: z
        .boolean()
        .describe('Whether the item is in stock.')
        .optional()
        .nullable(),
      taxRate: z
        .string()
        .describe('Tax rate of the item in percent.')
        .optional()
        .nullable(),
      archived: z
        .boolean()
        .describe(
          "Whether the item is archived. Defaults to `false`. **Note:** Archived items can't be updated."
        )
        .optional()
        .nullable(),
      visibilityCriteria: z
        .object({
          visible: z
            .boolean()
            .describe(
              "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
            )
            .optional()
            .nullable(),
          fulfillmentTypes: z
            .array(
              z.enum([
                'UNSPECIFIED_FULFILLMENT_TYPE',
                'DELIVERY',
                'PICKUP_OR_DINE_IN',
              ])
            )
            .min(1)
            .max(2)
            .optional(),
          platforms: z
            .array(
              z.enum([
                'UNSPECIFIED_PLATFORM',
                'SITE',
                'MOBILE_SITE',
                'WIX_APP',
                'CALL_CENTER',
                'CHAT_BOT',
              ])
            )
            .min(1)
            .max(5)
            .optional(),
          availability: z
            .object({
              periods: z
                .array(
                  z.object({
                    openDay: z
                      .enum([
                        'UNDEFINED',
                        'SUN',
                        'MON',
                        'TUE',
                        'WED',
                        'THU',
                        'FRI',
                        'SAT',
                      ])
                      .describe('Day of the week the period starts on.')
                      .optional(),
                    openTime: z
                      .string()
                      .describe(
                        'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                      )
                      .optional(),
                    closeDay: z
                      .enum([
                        'UNDEFINED',
                        'SUN',
                        'MON',
                        'TUE',
                        'WED',
                        'THU',
                        'FRI',
                        'SAT',
                      ])
                      .describe('Day of the week the period ends on.')
                      .optional(),
                    closeTime: z
                      .string()
                      .describe(
                        'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                      )
                      .optional(),
                  })
                )
                .optional(),
              specialHourPeriods: z
                .array(
                  z.object({
                    startDate: z
                      .string()
                      .describe(
                        'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                      )
                      .optional(),
                    endDate: z
                      .string()
                      .describe(
                        'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                      )
                      .optional(),
                    available: z
                      .boolean()
                      .describe(
                        'Whether the item is available during the exception. Defaults to `true`.'
                      )
                      .optional(),
                    eventName: z
                      .string()
                      .describe(
                        'Name of the special hour period. In the dashboard, the special hour period is called event.'
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .optional(),
            })
            .describe('Time periods when the entity is available.')
            .optional(),
        })
        .describe(
          'Visibility criteria that must be met for the item to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
        )
        .optional(),
      dishOptions: z
        .object({
          values: z
            .array(
              z.intersection(
                z.object({
                  name: z.string().describe('Dish option name.').optional(),
                  type: z
                    .enum([
                      'UNSPECIFIED_DISH_OPTION_TYPE',
                      'SELECTION',
                      'EXTRAS',
                      'DESELECTION',
                    ])
                    .optional(),
                }),
                z.xor([
                  z.object({
                    selection: z.never().optional(),
                    extras: z.never().optional(),
                    deselection: z.never().optional(),
                  }),
                  z.object({
                    extras: z.never().optional(),
                    deselection: z.never().optional(),
                    selection: z
                      .object({
                        defaultChoice: z
                          .string()
                          .describe('Item ID of the default choice.')
                          .optional()
                          .nullable(),
                        availableChoices: z
                          .array(
                            z.object({
                              itemId: z
                                .string()
                                .describe('Item ID of the dish option.')
                                .optional(),
                              price: z
                                .object({
                                  value: z
                                    .string()
                                    .describe(
                                      'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                    )
                                    .optional(),
                                  currency: z
                                    .string()
                                    .describe(
                                      'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  "Dish option price. `0` for free choices that are included in the dish's price."
                                )
                                .optional(),
                            })
                          )
                          .optional(),
                      })
                      .describe(
                        'Item of type `DISH` or `VARIATION` that customers can select. For example a dish size. Customers can choose only a single selection per dish.'
                      ),
                  }),
                  z.object({
                    selection: z.never().optional(),
                    deselection: z.never().optional(),
                    extras: z
                      .object({
                        defaultChoices: z.array(z.string()).optional(),
                        minChoices: z
                          .number()
                          .int()
                          .describe(
                            'Minimum number of extras a customer must choose. Defaults to `0`. Must be lower than or equal to the value of `availableChoices`.'
                          )
                          .min(0)
                          .max(2147483647)
                          .optional()
                          .nullable(),
                        maxChoices: z
                          .number()
                          .int()
                          .describe(
                            'Maximum number of extras a customer can choose. Defaults to the value of `availableChoices`. Must be greater than or equal to the value of `minChoices`.'
                          )
                          .min(0)
                          .max(2147483647)
                          .optional()
                          .nullable(),
                        availableChoices: z
                          .array(
                            z.object({
                              itemId: z
                                .string()
                                .describe('Item ID of the dish option.')
                                .optional(),
                              price: z
                                .object({
                                  value: z
                                    .string()
                                    .describe(
                                      'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                    )
                                    .optional(),
                                  currency: z
                                    .string()
                                    .describe(
                                      'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  "Dish option price. `0` for free choices that are included in the dish's price."
                                )
                                .optional(),
                            })
                          )
                          .optional(),
                      })
                      .describe(
                        'Item of type `DISH` or `VARIATION` that customers can add to a dish. For example a topping. Customers can add multiple extras per dish.'
                      ),
                  }),
                  z.object({
                    selection: z.never().optional(),
                    extras: z.never().optional(),
                    deselection: z
                      .object({
                        availableChoices: z
                          .array(
                            z.object({
                              itemId: z
                                .string()
                                .describe('Item ID of the dish option.')
                                .optional(),
                              price: z
                                .object({
                                  value: z
                                    .string()
                                    .describe(
                                      'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                    )
                                    .optional(),
                                  currency: z
                                    .string()
                                    .describe(
                                      'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  "Dish option price. `0` for free choices that are included in the dish's price."
                                )
                                .optional(),
                            })
                          )
                          .optional(),
                      })
                      .describe(
                        'Item of type `DISH` or `VARIATION` that customers can remove from a dish. For example a specific ingredient. Customers can remove multiple deselections per dish.'
                      ),
                  }),
                ])
              )
            )
            .optional(),
        })
        .describe(
          'Items customers can choose to modify a dish. Can be an extra, selection, or deselection.'
        )
        .optional(),
      acceptSpecialRequest: z
        .boolean()
        .describe(
          'Whether a customer can add a special request when ordering this item. Defaults to `true`.'
        )
        .optional()
        .nullable(),
      type: z
        .enum(['UNSPECIFIED_ITEM_TYPE', 'DISH', 'VARIATION'])
        .describe('Item type.')
        .optional(),
    })
    .describe('Updated Item.')
    .optional(),
});
export const CreateDishRequest = z.object({
  identifiers: z.object({
    catalogId: z
      .string()
      .describe('ID of the catalog the dish will belong to.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
    menuId: z.string().describe('ID of the menu the dish will belong to.'),
    sectionId: z
      .string()
      .describe('ID of the section the dish will belong to.'),
  }),
  dish: z
    .object({
      _id: z.string().describe('Item ID.').optional().nullable(),
      name: z.string().describe('Item name.').optional().nullable(),
      description: z
        .string()
        .describe('Item description.')
        .optional()
        .nullable(),
      price: z
        .object({
          value: z
            .string()
            .describe(
              'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
            )
            .optional(),
          currency: z
            .string()
            .describe(
              'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
            )
            .optional(),
        })
        .describe('Item price.')
        .optional(),
      imageUrl: z
        .string()
        .describe("URL of the item's image file.")
        .optional()
        .nullable(),
      labels: z
        .object({ values: z.array(z.string()).optional() })
        .describe(
          'Item labels. For example spicy, hot, vegan, gluten-free, or organic.'
        )
        .optional(),
      inStock: z
        .boolean()
        .describe('Whether the item is in stock.')
        .optional()
        .nullable(),
      taxRate: z
        .string()
        .describe('Tax rate of the item in percent.')
        .optional()
        .nullable(),
      archived: z
        .boolean()
        .describe(
          "Whether the item is archived. Defaults to `false`. **Note:** Archived items can't be updated."
        )
        .optional()
        .nullable(),
      visibilityCriteria: z
        .object({
          visible: z
            .boolean()
            .describe(
              "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
            )
            .optional()
            .nullable(),
          fulfillmentTypes: z
            .array(
              z.enum([
                'UNSPECIFIED_FULFILLMENT_TYPE',
                'DELIVERY',
                'PICKUP_OR_DINE_IN',
              ])
            )
            .min(1)
            .max(2)
            .optional(),
          platforms: z
            .array(
              z.enum([
                'UNSPECIFIED_PLATFORM',
                'SITE',
                'MOBILE_SITE',
                'WIX_APP',
                'CALL_CENTER',
                'CHAT_BOT',
              ])
            )
            .min(1)
            .max(5)
            .optional(),
          availability: z
            .object({
              periods: z
                .array(
                  z.object({
                    openDay: z
                      .enum([
                        'UNDEFINED',
                        'SUN',
                        'MON',
                        'TUE',
                        'WED',
                        'THU',
                        'FRI',
                        'SAT',
                      ])
                      .optional(),
                    openTime: z
                      .string()
                      .describe(
                        'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                      )
                      .optional(),
                    closeDay: z
                      .enum([
                        'UNDEFINED',
                        'SUN',
                        'MON',
                        'TUE',
                        'WED',
                        'THU',
                        'FRI',
                        'SAT',
                      ])
                      .optional(),
                    closeTime: z
                      .string()
                      .describe(
                        'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                      )
                      .optional(),
                  })
                )
                .optional(),
              specialHourPeriods: z
                .array(
                  z.object({
                    startDate: z
                      .string()
                      .describe(
                        'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                      )
                      .optional(),
                    endDate: z
                      .string()
                      .describe(
                        'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                      )
                      .optional(),
                    available: z
                      .boolean()
                      .describe(
                        'Whether the item is available during the exception. Defaults to `true`.'
                      )
                      .optional(),
                    eventName: z
                      .string()
                      .describe(
                        'Name of the special hour period. In the dashboard, the special hour period is called event.'
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .optional(),
            })
            .describe('Time periods when the entity is available.')
            .optional(),
        })
        .describe(
          'Visibility criteria that must be met for the item to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
        )
        .optional(),
      dishOptions: z
        .object({
          values: z
            .array(
              z.intersection(
                z.object({
                  name: z.string().describe('Dish option name.').optional(),
                  type: z
                    .enum([
                      'UNSPECIFIED_DISH_OPTION_TYPE',
                      'SELECTION',
                      'EXTRAS',
                      'DESELECTION',
                    ])
                    .optional(),
                }),
                z.xor([
                  z.object({
                    selection: z.never().optional(),
                    extras: z.never().optional(),
                    deselection: z.never().optional(),
                  }),
                  z.object({
                    extras: z.never().optional(),
                    deselection: z.never().optional(),
                    selection: z
                      .object({
                        defaultChoice: z
                          .string()
                          .describe('Item ID of the default choice.')
                          .optional()
                          .nullable(),
                        availableChoices: z
                          .array(
                            z.object({
                              itemId: z
                                .string()
                                .describe('Item ID of the dish option.')
                                .optional(),
                              price: z
                                .object({
                                  value: z
                                    .string()
                                    .describe(
                                      'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                    )
                                    .optional(),
                                  currency: z
                                    .string()
                                    .describe(
                                      'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  "Dish option price. `0` for free choices that are included in the dish's price."
                                )
                                .optional(),
                            })
                          )
                          .optional(),
                      })
                      .describe(
                        'Item of type `DISH` or `VARIATION` that customers can select. For example a dish size. Customers can choose only a single selection per dish.'
                      ),
                  }),
                  z.object({
                    selection: z.never().optional(),
                    deselection: z.never().optional(),
                    extras: z
                      .object({
                        defaultChoices: z.array(z.string()).optional(),
                        minChoices: z
                          .number()
                          .int()
                          .describe(
                            'Minimum number of extras a customer must choose. Defaults to `0`. Must be lower than or equal to the value of `availableChoices`.'
                          )
                          .min(0)
                          .max(2147483647)
                          .optional()
                          .nullable(),
                        maxChoices: z
                          .number()
                          .int()
                          .describe(
                            'Maximum number of extras a customer can choose. Defaults to the value of `availableChoices`. Must be greater than or equal to the value of `minChoices`.'
                          )
                          .min(0)
                          .max(2147483647)
                          .optional()
                          .nullable(),
                        availableChoices: z
                          .array(
                            z.object({
                              itemId: z
                                .string()
                                .describe('Item ID of the dish option.')
                                .optional(),
                              price: z
                                .object({
                                  value: z
                                    .string()
                                    .describe(
                                      'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                    )
                                    .optional(),
                                  currency: z
                                    .string()
                                    .describe(
                                      'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  "Dish option price. `0` for free choices that are included in the dish's price."
                                )
                                .optional(),
                            })
                          )
                          .optional(),
                      })
                      .describe(
                        'Item of type `DISH` or `VARIATION` that customers can add to a dish. For example a topping. Customers can add multiple extras per dish.'
                      ),
                  }),
                  z.object({
                    selection: z.never().optional(),
                    extras: z.never().optional(),
                    deselection: z
                      .object({
                        availableChoices: z
                          .array(
                            z.object({
                              itemId: z
                                .string()
                                .describe('Item ID of the dish option.')
                                .optional(),
                              price: z
                                .object({
                                  value: z
                                    .string()
                                    .describe(
                                      'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                    )
                                    .optional(),
                                  currency: z
                                    .string()
                                    .describe(
                                      'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  "Dish option price. `0` for free choices that are included in the dish's price."
                                )
                                .optional(),
                            })
                          )
                          .optional(),
                      })
                      .describe(
                        'Item of type `DISH` or `VARIATION` that customers can remove from a dish. For example a specific ingredient. Customers can remove multiple deselections per dish.'
                      ),
                  }),
                ])
              )
            )
            .optional(),
        })
        .describe(
          'Items customers can choose to modify a dish. Can be an extra, selection, or deselection.'
        )
        .optional(),
      acceptSpecialRequest: z
        .boolean()
        .describe(
          'Whether a customer can add a special request when ordering this item. Defaults to `true`.'
        )
        .optional()
        .nullable(),
      type: z.enum(['UNSPECIFIED_ITEM_TYPE', 'DISH', 'VARIATION']).optional(),
    })
    .describe('Item of type `dish` to create.'),
});
export const CreateDishResponse = z.object({
  dish: z
    .object({
      _id: z.string().describe('Item ID.').optional().nullable(),
      name: z.string().describe('Item name.').optional().nullable(),
      description: z
        .string()
        .describe('Item description.')
        .optional()
        .nullable(),
      price: z
        .object({
          value: z
            .string()
            .describe(
              'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
            )
            .optional(),
          currency: z
            .string()
            .describe(
              'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
            )
            .optional(),
        })
        .describe('Item price.')
        .optional(),
      imageUrl: z
        .string()
        .describe("URL of the item's image file.")
        .optional()
        .nullable(),
      labels: z
        .object({ values: z.array(z.string()).optional() })
        .describe(
          'Item labels. For example spicy, hot, vegan, gluten-free, or organic.'
        )
        .optional(),
      inStock: z
        .boolean()
        .describe('Whether the item is in stock.')
        .optional()
        .nullable(),
      taxRate: z
        .string()
        .describe('Tax rate of the item in percent.')
        .optional()
        .nullable(),
      archived: z
        .boolean()
        .describe(
          "Whether the item is archived. Defaults to `false`. **Note:** Archived items can't be updated."
        )
        .optional()
        .nullable(),
      visibilityCriteria: z
        .object({
          visible: z
            .boolean()
            .describe(
              "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
            )
            .optional()
            .nullable(),
          fulfillmentTypes: z
            .array(
              z.enum([
                'UNSPECIFIED_FULFILLMENT_TYPE',
                'DELIVERY',
                'PICKUP_OR_DINE_IN',
              ])
            )
            .min(1)
            .max(2)
            .optional(),
          platforms: z
            .array(
              z.enum([
                'UNSPECIFIED_PLATFORM',
                'SITE',
                'MOBILE_SITE',
                'WIX_APP',
                'CALL_CENTER',
                'CHAT_BOT',
              ])
            )
            .min(1)
            .max(5)
            .optional(),
          availability: z
            .object({
              periods: z
                .array(
                  z.object({
                    openDay: z
                      .enum([
                        'UNDEFINED',
                        'SUN',
                        'MON',
                        'TUE',
                        'WED',
                        'THU',
                        'FRI',
                        'SAT',
                      ])
                      .describe('Day of the week the period starts on.')
                      .optional(),
                    openTime: z
                      .string()
                      .describe(
                        'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                      )
                      .optional(),
                    closeDay: z
                      .enum([
                        'UNDEFINED',
                        'SUN',
                        'MON',
                        'TUE',
                        'WED',
                        'THU',
                        'FRI',
                        'SAT',
                      ])
                      .describe('Day of the week the period ends on.')
                      .optional(),
                    closeTime: z
                      .string()
                      .describe(
                        'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                      )
                      .optional(),
                  })
                )
                .optional(),
              specialHourPeriods: z
                .array(
                  z.object({
                    startDate: z
                      .string()
                      .describe(
                        'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                      )
                      .optional(),
                    endDate: z
                      .string()
                      .describe(
                        'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                      )
                      .optional(),
                    available: z
                      .boolean()
                      .describe(
                        'Whether the item is available during the exception. Defaults to `true`.'
                      )
                      .optional(),
                    eventName: z
                      .string()
                      .describe(
                        'Name of the special hour period. In the dashboard, the special hour period is called event.'
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .optional(),
            })
            .describe('Time periods when the entity is available.')
            .optional(),
        })
        .describe(
          'Visibility criteria that must be met for the item to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
        )
        .optional(),
      dishOptions: z
        .object({
          values: z
            .array(
              z.intersection(
                z.object({
                  name: z.string().describe('Dish option name.').optional(),
                  type: z
                    .enum([
                      'UNSPECIFIED_DISH_OPTION_TYPE',
                      'SELECTION',
                      'EXTRAS',
                      'DESELECTION',
                    ])
                    .optional(),
                }),
                z.xor([
                  z.object({
                    selection: z.never().optional(),
                    extras: z.never().optional(),
                    deselection: z.never().optional(),
                  }),
                  z.object({
                    extras: z.never().optional(),
                    deselection: z.never().optional(),
                    selection: z
                      .object({
                        defaultChoice: z
                          .string()
                          .describe('Item ID of the default choice.')
                          .optional()
                          .nullable(),
                        availableChoices: z
                          .array(
                            z.object({
                              itemId: z
                                .string()
                                .describe('Item ID of the dish option.')
                                .optional(),
                              price: z
                                .object({
                                  value: z
                                    .string()
                                    .describe(
                                      'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                    )
                                    .optional(),
                                  currency: z
                                    .string()
                                    .describe(
                                      'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  "Dish option price. `0` for free choices that are included in the dish's price."
                                )
                                .optional(),
                            })
                          )
                          .optional(),
                      })
                      .describe(
                        'Item of type `DISH` or `VARIATION` that customers can select. For example a dish size. Customers can choose only a single selection per dish.'
                      ),
                  }),
                  z.object({
                    selection: z.never().optional(),
                    deselection: z.never().optional(),
                    extras: z
                      .object({
                        defaultChoices: z.array(z.string()).optional(),
                        minChoices: z
                          .number()
                          .int()
                          .describe(
                            'Minimum number of extras a customer must choose. Defaults to `0`. Must be lower than or equal to the value of `availableChoices`.'
                          )
                          .min(0)
                          .max(2147483647)
                          .optional()
                          .nullable(),
                        maxChoices: z
                          .number()
                          .int()
                          .describe(
                            'Maximum number of extras a customer can choose. Defaults to the value of `availableChoices`. Must be greater than or equal to the value of `minChoices`.'
                          )
                          .min(0)
                          .max(2147483647)
                          .optional()
                          .nullable(),
                        availableChoices: z
                          .array(
                            z.object({
                              itemId: z
                                .string()
                                .describe('Item ID of the dish option.')
                                .optional(),
                              price: z
                                .object({
                                  value: z
                                    .string()
                                    .describe(
                                      'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                    )
                                    .optional(),
                                  currency: z
                                    .string()
                                    .describe(
                                      'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  "Dish option price. `0` for free choices that are included in the dish's price."
                                )
                                .optional(),
                            })
                          )
                          .optional(),
                      })
                      .describe(
                        'Item of type `DISH` or `VARIATION` that customers can add to a dish. For example a topping. Customers can add multiple extras per dish.'
                      ),
                  }),
                  z.object({
                    selection: z.never().optional(),
                    extras: z.never().optional(),
                    deselection: z
                      .object({
                        availableChoices: z
                          .array(
                            z.object({
                              itemId: z
                                .string()
                                .describe('Item ID of the dish option.')
                                .optional(),
                              price: z
                                .object({
                                  value: z
                                    .string()
                                    .describe(
                                      'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                    )
                                    .optional(),
                                  currency: z
                                    .string()
                                    .describe(
                                      'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  "Dish option price. `0` for free choices that are included in the dish's price."
                                )
                                .optional(),
                            })
                          )
                          .optional(),
                      })
                      .describe(
                        'Item of type `DISH` or `VARIATION` that customers can remove from a dish. For example a specific ingredient. Customers can remove multiple deselections per dish.'
                      ),
                  }),
                ])
              )
            )
            .optional(),
        })
        .describe(
          'Items customers can choose to modify a dish. Can be an extra, selection, or deselection.'
        )
        .optional(),
      acceptSpecialRequest: z
        .boolean()
        .describe(
          'Whether a customer can add a special request when ordering this item. Defaults to `true`.'
        )
        .optional()
        .nullable(),
      type: z
        .enum(['UNSPECIFIED_ITEM_TYPE', 'DISH', 'VARIATION'])
        .describe('Item type.')
        .optional(),
    })
    .describe('Created Dish.')
    .optional(),
});
export const CreateVariationRequest = z.object({
  catalogId: z
    .string()
    .describe('ID of the catalog the variation will belong to.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  name: z.string().describe('Variation name.'),
});
export const CreateVariationResponse = z.object({
  variation: z
    .object({
      _id: z.string().describe('Item ID.').optional().nullable(),
      name: z.string().describe('Item name.').optional().nullable(),
      description: z
        .string()
        .describe('Item description.')
        .optional()
        .nullable(),
      price: z
        .object({
          value: z
            .string()
            .describe(
              'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
            )
            .optional(),
          currency: z
            .string()
            .describe(
              'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
            )
            .optional(),
        })
        .describe('Item price.')
        .optional(),
      imageUrl: z
        .string()
        .describe("URL of the item's image file.")
        .optional()
        .nullable(),
      labels: z
        .object({ values: z.array(z.string()).optional() })
        .describe(
          'Item labels. For example spicy, hot, vegan, gluten-free, or organic.'
        )
        .optional(),
      inStock: z
        .boolean()
        .describe('Whether the item is in stock.')
        .optional()
        .nullable(),
      taxRate: z
        .string()
        .describe('Tax rate of the item in percent.')
        .optional()
        .nullable(),
      archived: z
        .boolean()
        .describe(
          "Whether the item is archived. Defaults to `false`. **Note:** Archived items can't be updated."
        )
        .optional()
        .nullable(),
      visibilityCriteria: z
        .object({
          visible: z
            .boolean()
            .describe(
              "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
            )
            .optional()
            .nullable(),
          fulfillmentTypes: z
            .array(
              z.enum([
                'UNSPECIFIED_FULFILLMENT_TYPE',
                'DELIVERY',
                'PICKUP_OR_DINE_IN',
              ])
            )
            .min(1)
            .max(2)
            .optional(),
          platforms: z
            .array(
              z.enum([
                'UNSPECIFIED_PLATFORM',
                'SITE',
                'MOBILE_SITE',
                'WIX_APP',
                'CALL_CENTER',
                'CHAT_BOT',
              ])
            )
            .min(1)
            .max(5)
            .optional(),
          availability: z
            .object({
              periods: z
                .array(
                  z.object({
                    openDay: z
                      .enum([
                        'UNDEFINED',
                        'SUN',
                        'MON',
                        'TUE',
                        'WED',
                        'THU',
                        'FRI',
                        'SAT',
                      ])
                      .describe('Day of the week the period starts on.')
                      .optional(),
                    openTime: z
                      .string()
                      .describe(
                        'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                      )
                      .optional(),
                    closeDay: z
                      .enum([
                        'UNDEFINED',
                        'SUN',
                        'MON',
                        'TUE',
                        'WED',
                        'THU',
                        'FRI',
                        'SAT',
                      ])
                      .describe('Day of the week the period ends on.')
                      .optional(),
                    closeTime: z
                      .string()
                      .describe(
                        'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                      )
                      .optional(),
                  })
                )
                .optional(),
              specialHourPeriods: z
                .array(
                  z.object({
                    startDate: z
                      .string()
                      .describe(
                        'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                      )
                      .optional(),
                    endDate: z
                      .string()
                      .describe(
                        'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                      )
                      .optional(),
                    available: z
                      .boolean()
                      .describe(
                        'Whether the item is available during the exception. Defaults to `true`.'
                      )
                      .optional(),
                    eventName: z
                      .string()
                      .describe(
                        'Name of the special hour period. In the dashboard, the special hour period is called event.'
                      )
                      .optional()
                      .nullable(),
                  })
                )
                .optional(),
            })
            .describe('Time periods when the entity is available.')
            .optional(),
        })
        .describe(
          'Visibility criteria that must be met for the item to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
        )
        .optional(),
      dishOptions: z
        .object({
          values: z
            .array(
              z.intersection(
                z.object({
                  name: z.string().describe('Dish option name.').optional(),
                  type: z
                    .enum([
                      'UNSPECIFIED_DISH_OPTION_TYPE',
                      'SELECTION',
                      'EXTRAS',
                      'DESELECTION',
                    ])
                    .optional(),
                }),
                z.xor([
                  z.object({
                    selection: z.never().optional(),
                    extras: z.never().optional(),
                    deselection: z.never().optional(),
                  }),
                  z.object({
                    extras: z.never().optional(),
                    deselection: z.never().optional(),
                    selection: z
                      .object({
                        defaultChoice: z
                          .string()
                          .describe('Item ID of the default choice.')
                          .optional()
                          .nullable(),
                        availableChoices: z
                          .array(
                            z.object({
                              itemId: z
                                .string()
                                .describe('Item ID of the dish option.')
                                .optional(),
                              price: z
                                .object({
                                  value: z
                                    .string()
                                    .describe(
                                      'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                    )
                                    .optional(),
                                  currency: z
                                    .string()
                                    .describe(
                                      'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  "Dish option price. `0` for free choices that are included in the dish's price."
                                )
                                .optional(),
                            })
                          )
                          .optional(),
                      })
                      .describe(
                        'Item of type `DISH` or `VARIATION` that customers can select. For example a dish size. Customers can choose only a single selection per dish.'
                      ),
                  }),
                  z.object({
                    selection: z.never().optional(),
                    deselection: z.never().optional(),
                    extras: z
                      .object({
                        defaultChoices: z.array(z.string()).optional(),
                        minChoices: z
                          .number()
                          .int()
                          .describe(
                            'Minimum number of extras a customer must choose. Defaults to `0`. Must be lower than or equal to the value of `availableChoices`.'
                          )
                          .min(0)
                          .max(2147483647)
                          .optional()
                          .nullable(),
                        maxChoices: z
                          .number()
                          .int()
                          .describe(
                            'Maximum number of extras a customer can choose. Defaults to the value of `availableChoices`. Must be greater than or equal to the value of `minChoices`.'
                          )
                          .min(0)
                          .max(2147483647)
                          .optional()
                          .nullable(),
                        availableChoices: z
                          .array(
                            z.object({
                              itemId: z
                                .string()
                                .describe('Item ID of the dish option.')
                                .optional(),
                              price: z
                                .object({
                                  value: z
                                    .string()
                                    .describe(
                                      'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                    )
                                    .optional(),
                                  currency: z
                                    .string()
                                    .describe(
                                      'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  "Dish option price. `0` for free choices that are included in the dish's price."
                                )
                                .optional(),
                            })
                          )
                          .optional(),
                      })
                      .describe(
                        'Item of type `DISH` or `VARIATION` that customers can add to a dish. For example a topping. Customers can add multiple extras per dish.'
                      ),
                  }),
                  z.object({
                    selection: z.never().optional(),
                    extras: z.never().optional(),
                    deselection: z
                      .object({
                        availableChoices: z
                          .array(
                            z.object({
                              itemId: z
                                .string()
                                .describe('Item ID of the dish option.')
                                .optional(),
                              price: z
                                .object({
                                  value: z
                                    .string()
                                    .describe(
                                      'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                    )
                                    .optional(),
                                  currency: z
                                    .string()
                                    .describe(
                                      'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                    )
                                    .optional(),
                                })
                                .describe(
                                  "Dish option price. `0` for free choices that are included in the dish's price."
                                )
                                .optional(),
                            })
                          )
                          .optional(),
                      })
                      .describe(
                        'Item of type `DISH` or `VARIATION` that customers can remove from a dish. For example a specific ingredient. Customers can remove multiple deselections per dish.'
                      ),
                  }),
                ])
              )
            )
            .optional(),
        })
        .describe(
          'Items customers can choose to modify a dish. Can be an extra, selection, or deselection.'
        )
        .optional(),
      acceptSpecialRequest: z
        .boolean()
        .describe(
          'Whether a customer can add a special request when ordering this item. Defaults to `true`.'
        )
        .optional()
        .nullable(),
      type: z
        .enum(['UNSPECIFIED_ITEM_TYPE', 'DISH', 'VARIATION'])
        .describe('Item type.')
        .optional(),
    })
    .describe('Created Variation.')
    .optional(),
});
export const CreateDraftCatalogRequest = z.object({
  catalogId: z
    .string()
    .describe('ID of the catalog to create a draft version for.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const CreateDraftCatalogResponse = z.object({
  catalog: z
    .object({
      _id: z.string().describe('Catalog ID.').optional().nullable(),
      locationId: z
        .string()
        .describe(
          'ID of the location the catalog belongs to. See the [Locations API](https://dev.wix.com/api/rest/business-info/locations/introduction) for more details.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      archived: z
        .boolean()
        .describe(
          "Whether the catalog is archived.\n__Note:__ Archived catalogs can't be updated."
        )
        .optional(),
      draftPublishedDate: z
        .date()
        .describe(
          'Date and time the last time a draft catalog has been published in `yyyy-mm-ddThh:mm:sssZ` format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
        )
        .optional()
        .nullable(),
    })
    .describe('Created draft catalog.')
    .optional(),
});
export const PublishDraftCatalogRequest = z.object({
  catalogId: z
    .string()
    .describe('ID of the draft catalog to publish.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const PublishDraftCatalogResponse = z.object({
  catalog: z
    .object({
      _id: z.string().describe('Catalog ID.').optional().nullable(),
      locationId: z
        .string()
        .describe(
          'ID of the location the catalog belongs to. See the [Locations API](https://dev.wix.com/api/rest/business-info/locations/introduction) for more details.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      archived: z
        .boolean()
        .describe(
          "Whether the catalog is archived.\n__Note:__ Archived catalogs can't be updated."
        )
        .optional(),
      draftPublishedDate: z
        .date()
        .describe(
          'Date and time the last time a draft catalog has been published in `yyyy-mm-ddThh:mm:sssZ` format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
        )
        .optional()
        .nullable(),
    })
    .describe('Published catalog.')
    .optional(),
});
export const DiscardDraftCatalogRequest = z.object({
  catalogId: z
    .string()
    .describe('ID of the draft catalog to discard.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DiscardDraftCatalogResponse = z.object({});
export const BulkCreateVariationsRequest = z.object({
  catalogId: z
    .string()
    .describe('ID of the catalog the variations will belong to.'),
  options: z
    .object({
      variations: z
        .array(
          z.object({
            name: z.string().describe('Variation name.').min(1).optional(),
          })
        )
        .min(1)
        .max(20)
        .optional(),
      returnFullEntity: z
        .boolean()
        .describe(
          'Whether the full variation entity is returned. Defaults to `true`.'
        )
        .optional(),
    })
    .optional(),
});
export const BulkCreateVariationsResponse = z.object({
  results: z
    .array(
      z.object({
        entityMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata of the item.')
          .optional(),
        entity: z
          .object({
            _id: z.string().describe('Item ID.').optional().nullable(),
            name: z.string().describe('Item name.').optional().nullable(),
            description: z
              .string()
              .describe('Item description.')
              .optional()
              .nullable(),
            price: z
              .object({
                value: z
                  .string()
                  .describe(
                    'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                  )
                  .optional(),
                currency: z
                  .string()
                  .describe(
                    'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                  )
                  .optional(),
              })
              .describe('Item price.')
              .optional(),
            imageUrl: z
              .string()
              .describe("URL of the item's image file.")
              .optional()
              .nullable(),
            labels: z
              .object({ values: z.array(z.string()).optional() })
              .describe(
                'Item labels. For example spicy, hot, vegan, gluten-free, or organic.'
              )
              .optional(),
            inStock: z
              .boolean()
              .describe('Whether the item is in stock.')
              .optional()
              .nullable(),
            taxRate: z
              .string()
              .describe('Tax rate of the item in percent.')
              .optional()
              .nullable(),
            archived: z
              .boolean()
              .describe(
                "Whether the item is archived. Defaults to `false`. **Note:** Archived items can't be updated."
              )
              .optional()
              .nullable(),
            visibilityCriteria: z
              .object({
                visible: z
                  .boolean()
                  .describe(
                    "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
                  )
                  .optional()
                  .nullable(),
                fulfillmentTypes: z
                  .array(
                    z.enum([
                      'UNSPECIFIED_FULFILLMENT_TYPE',
                      'DELIVERY',
                      'PICKUP_OR_DINE_IN',
                    ])
                  )
                  .min(1)
                  .max(2)
                  .optional(),
                platforms: z
                  .array(
                    z.enum([
                      'UNSPECIFIED_PLATFORM',
                      'SITE',
                      'MOBILE_SITE',
                      'WIX_APP',
                      'CALL_CENTER',
                      'CHAT_BOT',
                    ])
                  )
                  .min(1)
                  .max(5)
                  .optional(),
                availability: z
                  .object({
                    periods: z
                      .array(
                        z.object({
                          openDay: z
                            .enum([
                              'UNDEFINED',
                              'SUN',
                              'MON',
                              'TUE',
                              'WED',
                              'THU',
                              'FRI',
                              'SAT',
                            ])
                            .describe('Day of the week the period starts on.')
                            .optional(),
                          openTime: z
                            .string()
                            .describe(
                              'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                            )
                            .optional(),
                          closeDay: z
                            .enum([
                              'UNDEFINED',
                              'SUN',
                              'MON',
                              'TUE',
                              'WED',
                              'THU',
                              'FRI',
                              'SAT',
                            ])
                            .describe('Day of the week the period ends on.')
                            .optional(),
                          closeTime: z
                            .string()
                            .describe(
                              'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                            )
                            .optional(),
                        })
                      )
                      .optional(),
                    specialHourPeriods: z
                      .array(
                        z.object({
                          startDate: z
                            .string()
                            .describe(
                              'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                            )
                            .optional(),
                          endDate: z
                            .string()
                            .describe(
                              'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                            )
                            .optional(),
                          available: z
                            .boolean()
                            .describe(
                              'Whether the item is available during the exception. Defaults to `true`.'
                            )
                            .optional(),
                          eventName: z
                            .string()
                            .describe(
                              'Name of the special hour period. In the dashboard, the special hour period is called event.'
                            )
                            .optional()
                            .nullable(),
                        })
                      )
                      .optional(),
                  })
                  .describe('Time periods when the entity is available.')
                  .optional(),
              })
              .describe(
                'Visibility criteria that must be met for the item to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
              )
              .optional(),
            dishOptions: z
              .object({
                values: z
                  .array(
                    z.intersection(
                      z.object({
                        name: z
                          .string()
                          .describe('Dish option name.')
                          .optional(),
                        type: z
                          .enum([
                            'UNSPECIFIED_DISH_OPTION_TYPE',
                            'SELECTION',
                            'EXTRAS',
                            'DESELECTION',
                          ])
                          .optional(),
                      }),
                      z.xor([
                        z.object({
                          selection: z.never().optional(),
                          extras: z.never().optional(),
                          deselection: z.never().optional(),
                        }),
                        z.object({
                          extras: z.never().optional(),
                          deselection: z.never().optional(),
                          selection: z
                            .object({
                              defaultChoice: z
                                .string()
                                .describe('Item ID of the default choice.')
                                .optional()
                                .nullable(),
                              availableChoices: z
                                .array(
                                  z.object({
                                    itemId: z
                                      .string()
                                      .describe('Item ID of the dish option.')
                                      .optional(),
                                    price: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe(
                                            'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                          )
                                          .optional(),
                                        currency: z
                                          .string()
                                          .describe(
                                            'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                          )
                                          .optional(),
                                      })
                                      .describe(
                                        "Dish option price. `0` for free choices that are included in the dish's price."
                                      )
                                      .optional(),
                                  })
                                )
                                .optional(),
                            })
                            .describe(
                              'Item of type `DISH` or `VARIATION` that customers can select. For example a dish size. Customers can choose only a single selection per dish.'
                            ),
                        }),
                        z.object({
                          selection: z.never().optional(),
                          deselection: z.never().optional(),
                          extras: z
                            .object({
                              defaultChoices: z.array(z.string()).optional(),
                              minChoices: z
                                .number()
                                .int()
                                .describe(
                                  'Minimum number of extras a customer must choose. Defaults to `0`. Must be lower than or equal to the value of `availableChoices`.'
                                )
                                .min(0)
                                .max(2147483647)
                                .optional()
                                .nullable(),
                              maxChoices: z
                                .number()
                                .int()
                                .describe(
                                  'Maximum number of extras a customer can choose. Defaults to the value of `availableChoices`. Must be greater than or equal to the value of `minChoices`.'
                                )
                                .min(0)
                                .max(2147483647)
                                .optional()
                                .nullable(),
                              availableChoices: z
                                .array(
                                  z.object({
                                    itemId: z
                                      .string()
                                      .describe('Item ID of the dish option.')
                                      .optional(),
                                    price: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe(
                                            'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                          )
                                          .optional(),
                                        currency: z
                                          .string()
                                          .describe(
                                            'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                          )
                                          .optional(),
                                      })
                                      .describe(
                                        "Dish option price. `0` for free choices that are included in the dish's price."
                                      )
                                      .optional(),
                                  })
                                )
                                .optional(),
                            })
                            .describe(
                              'Item of type `DISH` or `VARIATION` that customers can add to a dish. For example a topping. Customers can add multiple extras per dish.'
                            ),
                        }),
                        z.object({
                          selection: z.never().optional(),
                          extras: z.never().optional(),
                          deselection: z
                            .object({
                              availableChoices: z
                                .array(
                                  z.object({
                                    itemId: z
                                      .string()
                                      .describe('Item ID of the dish option.')
                                      .optional(),
                                    price: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe(
                                            'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                          )
                                          .optional(),
                                        currency: z
                                          .string()
                                          .describe(
                                            'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                          )
                                          .optional(),
                                      })
                                      .describe(
                                        "Dish option price. `0` for free choices that are included in the dish's price."
                                      )
                                      .optional(),
                                  })
                                )
                                .optional(),
                            })
                            .describe(
                              'Item of type `DISH` or `VARIATION` that customers can remove from a dish. For example a specific ingredient. Customers can remove multiple deselections per dish.'
                            ),
                        }),
                      ])
                    )
                  )
                  .optional(),
              })
              .describe(
                'Items customers can choose to modify a dish. Can be an extra, selection, or deselection.'
              )
              .optional(),
            acceptSpecialRequest: z
              .boolean()
              .describe(
                'Whether a customer can add a special request when ordering this item. Defaults to `true`.'
              )
              .optional()
              .nullable(),
            type: z
              .enum(['UNSPECIFIED_ITEM_TYPE', 'DISH', 'VARIATION'])
              .describe('Item type.')
              .optional(),
          })
          .describe('Item.')
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk Create Variations metadata.')
    .optional(),
});
export const BulkUpdateItemsRequest = z.object({
  catalogId: z.string().describe('ID of the catalog the items belong to.'),
  options: z
    .object({
      items: z
        .array(
          z.object({
            _id: z.string().describe('Item ID.').optional().nullable(),
            name: z.string().describe('Item name.').optional().nullable(),
            description: z
              .string()
              .describe('Item description.')
              .optional()
              .nullable(),
            price: z
              .object({
                value: z
                  .string()
                  .describe(
                    'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                  )
                  .optional(),
                currency: z
                  .string()
                  .describe(
                    'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                  )
                  .optional(),
              })
              .describe('Item price.')
              .optional(),
            imageUrl: z
              .string()
              .describe("URL of the item's image file.")
              .optional()
              .nullable(),
            labels: z
              .object({ values: z.array(z.string()).optional() })
              .describe(
                'Item labels. For example spicy, hot, vegan, gluten-free, or organic.'
              )
              .optional(),
            inStock: z
              .boolean()
              .describe('Whether the item is in stock.')
              .optional()
              .nullable(),
            taxRate: z
              .string()
              .describe('Tax rate of the item in percent.')
              .optional()
              .nullable(),
            archived: z
              .boolean()
              .describe(
                "Whether the item is archived. Defaults to `false`. **Note:** Archived items can't be updated."
              )
              .optional()
              .nullable(),
            visibilityCriteria: z
              .object({
                visible: z
                  .boolean()
                  .describe(
                    "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
                  )
                  .optional()
                  .nullable(),
                fulfillmentTypes: z
                  .array(
                    z.enum([
                      'UNSPECIFIED_FULFILLMENT_TYPE',
                      'DELIVERY',
                      'PICKUP_OR_DINE_IN',
                    ])
                  )
                  .min(1)
                  .max(2)
                  .optional(),
                platforms: z
                  .array(
                    z.enum([
                      'UNSPECIFIED_PLATFORM',
                      'SITE',
                      'MOBILE_SITE',
                      'WIX_APP',
                      'CALL_CENTER',
                      'CHAT_BOT',
                    ])
                  )
                  .min(1)
                  .max(5)
                  .optional(),
                availability: z
                  .object({
                    periods: z
                      .array(
                        z.object({
                          openDay: z
                            .enum([
                              'UNDEFINED',
                              'SUN',
                              'MON',
                              'TUE',
                              'WED',
                              'THU',
                              'FRI',
                              'SAT',
                            ])
                            .optional(),
                          openTime: z
                            .string()
                            .describe(
                              'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                            )
                            .optional(),
                          closeDay: z
                            .enum([
                              'UNDEFINED',
                              'SUN',
                              'MON',
                              'TUE',
                              'WED',
                              'THU',
                              'FRI',
                              'SAT',
                            ])
                            .optional(),
                          closeTime: z
                            .string()
                            .describe(
                              'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                            )
                            .optional(),
                        })
                      )
                      .optional(),
                    specialHourPeriods: z
                      .array(
                        z.object({
                          startDate: z
                            .string()
                            .describe(
                              'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                            )
                            .optional(),
                          endDate: z
                            .string()
                            .describe(
                              'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                            )
                            .optional(),
                          available: z
                            .boolean()
                            .describe(
                              'Whether the item is available during the exception. Defaults to `true`.'
                            )
                            .optional(),
                          eventName: z
                            .string()
                            .describe(
                              'Name of the special hour period. In the dashboard, the special hour period is called event.'
                            )
                            .optional()
                            .nullable(),
                        })
                      )
                      .optional(),
                  })
                  .describe('Time periods when the entity is available.')
                  .optional(),
              })
              .describe(
                'Visibility criteria that must be met for the item to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
              )
              .optional(),
            dishOptions: z
              .object({
                values: z
                  .array(
                    z.intersection(
                      z.object({
                        name: z
                          .string()
                          .describe('Dish option name.')
                          .optional(),
                        type: z
                          .enum([
                            'UNSPECIFIED_DISH_OPTION_TYPE',
                            'SELECTION',
                            'EXTRAS',
                            'DESELECTION',
                          ])
                          .optional(),
                      }),
                      z.xor([
                        z.object({
                          selection: z.never().optional(),
                          extras: z.never().optional(),
                          deselection: z.never().optional(),
                        }),
                        z.object({
                          extras: z.never().optional(),
                          deselection: z.never().optional(),
                          selection: z
                            .object({
                              defaultChoice: z
                                .string()
                                .describe('Item ID of the default choice.')
                                .optional()
                                .nullable(),
                              availableChoices: z
                                .array(
                                  z.object({
                                    itemId: z
                                      .string()
                                      .describe('Item ID of the dish option.')
                                      .optional(),
                                    price: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe(
                                            'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                          )
                                          .optional(),
                                        currency: z
                                          .string()
                                          .describe(
                                            'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                          )
                                          .optional(),
                                      })
                                      .describe(
                                        "Dish option price. `0` for free choices that are included in the dish's price."
                                      )
                                      .optional(),
                                  })
                                )
                                .optional(),
                            })
                            .describe(
                              'Item of type `DISH` or `VARIATION` that customers can select. For example a dish size. Customers can choose only a single selection per dish.'
                            ),
                        }),
                        z.object({
                          selection: z.never().optional(),
                          deselection: z.never().optional(),
                          extras: z
                            .object({
                              defaultChoices: z.array(z.string()).optional(),
                              minChoices: z
                                .number()
                                .int()
                                .describe(
                                  'Minimum number of extras a customer must choose. Defaults to `0`. Must be lower than or equal to the value of `availableChoices`.'
                                )
                                .min(0)
                                .max(2147483647)
                                .optional()
                                .nullable(),
                              maxChoices: z
                                .number()
                                .int()
                                .describe(
                                  'Maximum number of extras a customer can choose. Defaults to the value of `availableChoices`. Must be greater than or equal to the value of `minChoices`.'
                                )
                                .min(0)
                                .max(2147483647)
                                .optional()
                                .nullable(),
                              availableChoices: z
                                .array(
                                  z.object({
                                    itemId: z
                                      .string()
                                      .describe('Item ID of the dish option.')
                                      .optional(),
                                    price: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe(
                                            'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                          )
                                          .optional(),
                                        currency: z
                                          .string()
                                          .describe(
                                            'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                          )
                                          .optional(),
                                      })
                                      .describe(
                                        "Dish option price. `0` for free choices that are included in the dish's price."
                                      )
                                      .optional(),
                                  })
                                )
                                .optional(),
                            })
                            .describe(
                              'Item of type `DISH` or `VARIATION` that customers can add to a dish. For example a topping. Customers can add multiple extras per dish.'
                            ),
                        }),
                        z.object({
                          selection: z.never().optional(),
                          extras: z.never().optional(),
                          deselection: z
                            .object({
                              availableChoices: z
                                .array(
                                  z.object({
                                    itemId: z
                                      .string()
                                      .describe('Item ID of the dish option.')
                                      .optional(),
                                    price: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe(
                                            'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                          )
                                          .optional(),
                                        currency: z
                                          .string()
                                          .describe(
                                            'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                          )
                                          .optional(),
                                      })
                                      .describe(
                                        "Dish option price. `0` for free choices that are included in the dish's price."
                                      )
                                      .optional(),
                                  })
                                )
                                .optional(),
                            })
                            .describe(
                              'Item of type `DISH` or `VARIATION` that customers can remove from a dish. For example a specific ingredient. Customers can remove multiple deselections per dish.'
                            ),
                        }),
                      ])
                    )
                  )
                  .optional(),
              })
              .describe(
                'Items customers can choose to modify a dish. Can be an extra, selection, or deselection.'
              )
              .optional(),
            acceptSpecialRequest: z
              .boolean()
              .describe(
                'Whether a customer can add a special request when ordering this item. Defaults to `true`.'
              )
              .optional()
              .nullable(),
            type: z
              .enum(['UNSPECIFIED_ITEM_TYPE', 'DISH', 'VARIATION'])
              .optional(),
          })
        )
        .min(1)
        .max(20)
        .optional(),
      returnFullEntity: z
        .boolean()
        .describe(
          'Whether the full item entity is returned. Defaults to `true`.'
        )
        .optional(),
    })
    .optional(),
});
export const BulkUpdateItemsResponse = z.object({
  results: z
    .array(
      z.object({
        entityMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata of the item.')
          .optional(),
        entity: z
          .object({
            _id: z.string().describe('Item ID.').optional().nullable(),
            name: z.string().describe('Item name.').optional().nullable(),
            description: z
              .string()
              .describe('Item description.')
              .optional()
              .nullable(),
            price: z
              .object({
                value: z
                  .string()
                  .describe(
                    'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                  )
                  .optional(),
                currency: z
                  .string()
                  .describe(
                    'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                  )
                  .optional(),
              })
              .describe('Item price.')
              .optional(),
            imageUrl: z
              .string()
              .describe("URL of the item's image file.")
              .optional()
              .nullable(),
            labels: z
              .object({ values: z.array(z.string()).optional() })
              .describe(
                'Item labels. For example spicy, hot, vegan, gluten-free, or organic.'
              )
              .optional(),
            inStock: z
              .boolean()
              .describe('Whether the item is in stock.')
              .optional()
              .nullable(),
            taxRate: z
              .string()
              .describe('Tax rate of the item in percent.')
              .optional()
              .nullable(),
            archived: z
              .boolean()
              .describe(
                "Whether the item is archived. Defaults to `false`. **Note:** Archived items can't be updated."
              )
              .optional()
              .nullable(),
            visibilityCriteria: z
              .object({
                visible: z
                  .boolean()
                  .describe(
                    "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
                  )
                  .optional()
                  .nullable(),
                fulfillmentTypes: z
                  .array(
                    z.enum([
                      'UNSPECIFIED_FULFILLMENT_TYPE',
                      'DELIVERY',
                      'PICKUP_OR_DINE_IN',
                    ])
                  )
                  .min(1)
                  .max(2)
                  .optional(),
                platforms: z
                  .array(
                    z.enum([
                      'UNSPECIFIED_PLATFORM',
                      'SITE',
                      'MOBILE_SITE',
                      'WIX_APP',
                      'CALL_CENTER',
                      'CHAT_BOT',
                    ])
                  )
                  .min(1)
                  .max(5)
                  .optional(),
                availability: z
                  .object({
                    periods: z
                      .array(
                        z.object({
                          openDay: z
                            .enum([
                              'UNDEFINED',
                              'SUN',
                              'MON',
                              'TUE',
                              'WED',
                              'THU',
                              'FRI',
                              'SAT',
                            ])
                            .describe('Day of the week the period starts on.')
                            .optional(),
                          openTime: z
                            .string()
                            .describe(
                              'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                            )
                            .optional(),
                          closeDay: z
                            .enum([
                              'UNDEFINED',
                              'SUN',
                              'MON',
                              'TUE',
                              'WED',
                              'THU',
                              'FRI',
                              'SAT',
                            ])
                            .describe('Day of the week the period ends on.')
                            .optional(),
                          closeTime: z
                            .string()
                            .describe(
                              'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                            )
                            .optional(),
                        })
                      )
                      .optional(),
                    specialHourPeriods: z
                      .array(
                        z.object({
                          startDate: z
                            .string()
                            .describe(
                              'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                            )
                            .optional(),
                          endDate: z
                            .string()
                            .describe(
                              'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                            )
                            .optional(),
                          available: z
                            .boolean()
                            .describe(
                              'Whether the item is available during the exception. Defaults to `true`.'
                            )
                            .optional(),
                          eventName: z
                            .string()
                            .describe(
                              'Name of the special hour period. In the dashboard, the special hour period is called event.'
                            )
                            .optional()
                            .nullable(),
                        })
                      )
                      .optional(),
                  })
                  .describe('Time periods when the entity is available.')
                  .optional(),
              })
              .describe(
                'Visibility criteria that must be met for the item to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
              )
              .optional(),
            dishOptions: z
              .object({
                values: z
                  .array(
                    z.intersection(
                      z.object({
                        name: z
                          .string()
                          .describe('Dish option name.')
                          .optional(),
                        type: z
                          .enum([
                            'UNSPECIFIED_DISH_OPTION_TYPE',
                            'SELECTION',
                            'EXTRAS',
                            'DESELECTION',
                          ])
                          .optional(),
                      }),
                      z.xor([
                        z.object({
                          selection: z.never().optional(),
                          extras: z.never().optional(),
                          deselection: z.never().optional(),
                        }),
                        z.object({
                          extras: z.never().optional(),
                          deselection: z.never().optional(),
                          selection: z
                            .object({
                              defaultChoice: z
                                .string()
                                .describe('Item ID of the default choice.')
                                .optional()
                                .nullable(),
                              availableChoices: z
                                .array(
                                  z.object({
                                    itemId: z
                                      .string()
                                      .describe('Item ID of the dish option.')
                                      .optional(),
                                    price: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe(
                                            'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                          )
                                          .optional(),
                                        currency: z
                                          .string()
                                          .describe(
                                            'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                          )
                                          .optional(),
                                      })
                                      .describe(
                                        "Dish option price. `0` for free choices that are included in the dish's price."
                                      )
                                      .optional(),
                                  })
                                )
                                .optional(),
                            })
                            .describe(
                              'Item of type `DISH` or `VARIATION` that customers can select. For example a dish size. Customers can choose only a single selection per dish.'
                            ),
                        }),
                        z.object({
                          selection: z.never().optional(),
                          deselection: z.never().optional(),
                          extras: z
                            .object({
                              defaultChoices: z.array(z.string()).optional(),
                              minChoices: z
                                .number()
                                .int()
                                .describe(
                                  'Minimum number of extras a customer must choose. Defaults to `0`. Must be lower than or equal to the value of `availableChoices`.'
                                )
                                .min(0)
                                .max(2147483647)
                                .optional()
                                .nullable(),
                              maxChoices: z
                                .number()
                                .int()
                                .describe(
                                  'Maximum number of extras a customer can choose. Defaults to the value of `availableChoices`. Must be greater than or equal to the value of `minChoices`.'
                                )
                                .min(0)
                                .max(2147483647)
                                .optional()
                                .nullable(),
                              availableChoices: z
                                .array(
                                  z.object({
                                    itemId: z
                                      .string()
                                      .describe('Item ID of the dish option.')
                                      .optional(),
                                    price: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe(
                                            'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                          )
                                          .optional(),
                                        currency: z
                                          .string()
                                          .describe(
                                            'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                          )
                                          .optional(),
                                      })
                                      .describe(
                                        "Dish option price. `0` for free choices that are included in the dish's price."
                                      )
                                      .optional(),
                                  })
                                )
                                .optional(),
                            })
                            .describe(
                              'Item of type `DISH` or `VARIATION` that customers can add to a dish. For example a topping. Customers can add multiple extras per dish.'
                            ),
                        }),
                        z.object({
                          selection: z.never().optional(),
                          extras: z.never().optional(),
                          deselection: z
                            .object({
                              availableChoices: z
                                .array(
                                  z.object({
                                    itemId: z
                                      .string()
                                      .describe('Item ID of the dish option.')
                                      .optional(),
                                    price: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe(
                                            'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                          )
                                          .optional(),
                                        currency: z
                                          .string()
                                          .describe(
                                            'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                          )
                                          .optional(),
                                      })
                                      .describe(
                                        "Dish option price. `0` for free choices that are included in the dish's price."
                                      )
                                      .optional(),
                                  })
                                )
                                .optional(),
                            })
                            .describe(
                              'Item of type `DISH` or `VARIATION` that customers can remove from a dish. For example a specific ingredient. Customers can remove multiple deselections per dish.'
                            ),
                        }),
                      ])
                    )
                  )
                  .optional(),
              })
              .describe(
                'Items customers can choose to modify a dish. Can be an extra, selection, or deselection.'
              )
              .optional(),
            acceptSpecialRequest: z
              .boolean()
              .describe(
                'Whether a customer can add a special request when ordering this item. Defaults to `true`.'
              )
              .optional()
              .nullable(),
            type: z
              .enum(['UNSPECIFIED_ITEM_TYPE', 'DISH', 'VARIATION'])
              .describe('Item type.')
              .optional(),
          })
          .describe('Item.')
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk Update Items metadata.')
    .optional(),
});
export const BulkCreateDishesRequest = z.object({
  catalogId: z
    .string()
    .describe('ID of the catalog the dishes will belong to.'),
  options: z
    .object({
      createDishRequests: z
        .array(
          z.object({
            menuId: z
              .string()
              .describe('ID of the menu the dish will belong to.')
              .optional(),
            sectionId: z
              .string()
              .describe('ID of the section the dish will belong to.')
              .optional(),
            dish: z
              .object({
                _id: z.string().describe('Item ID.').optional().nullable(),
                name: z.string().describe('Item name.').optional().nullable(),
                description: z
                  .string()
                  .describe('Item description.')
                  .optional()
                  .nullable(),
                price: z
                  .object({
                    value: z
                      .string()
                      .describe(
                        'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                      )
                      .optional(),
                    currency: z
                      .string()
                      .describe(
                        'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                      )
                      .optional(),
                  })
                  .describe('Item price.')
                  .optional(),
                imageUrl: z
                  .string()
                  .describe("URL of the item's image file.")
                  .optional()
                  .nullable(),
                labels: z
                  .object({ values: z.array(z.string()).optional() })
                  .describe(
                    'Item labels. For example spicy, hot, vegan, gluten-free, or organic.'
                  )
                  .optional(),
                inStock: z
                  .boolean()
                  .describe('Whether the item is in stock.')
                  .optional()
                  .nullable(),
                taxRate: z
                  .string()
                  .describe('Tax rate of the item in percent.')
                  .optional()
                  .nullable(),
                archived: z
                  .boolean()
                  .describe(
                    "Whether the item is archived. Defaults to `false`. **Note:** Archived items can't be updated."
                  )
                  .optional()
                  .nullable(),
                visibilityCriteria: z
                  .object({
                    visible: z
                      .boolean()
                      .describe(
                        "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
                      )
                      .optional()
                      .nullable(),
                    fulfillmentTypes: z
                      .array(
                        z.enum([
                          'UNSPECIFIED_FULFILLMENT_TYPE',
                          'DELIVERY',
                          'PICKUP_OR_DINE_IN',
                        ])
                      )
                      .min(1)
                      .max(2)
                      .optional(),
                    platforms: z
                      .array(
                        z.enum([
                          'UNSPECIFIED_PLATFORM',
                          'SITE',
                          'MOBILE_SITE',
                          'WIX_APP',
                          'CALL_CENTER',
                          'CHAT_BOT',
                        ])
                      )
                      .min(1)
                      .max(5)
                      .optional(),
                    availability: z
                      .object({
                        periods: z
                          .array(
                            z.object({
                              openDay: z
                                .enum([
                                  'UNDEFINED',
                                  'SUN',
                                  'MON',
                                  'TUE',
                                  'WED',
                                  'THU',
                                  'FRI',
                                  'SAT',
                                ])
                                .optional(),
                              openTime: z
                                .string()
                                .describe(
                                  'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                                )
                                .optional(),
                              closeDay: z
                                .enum([
                                  'UNDEFINED',
                                  'SUN',
                                  'MON',
                                  'TUE',
                                  'WED',
                                  'THU',
                                  'FRI',
                                  'SAT',
                                ])
                                .optional(),
                              closeTime: z
                                .string()
                                .describe(
                                  'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                                )
                                .optional(),
                            })
                          )
                          .optional(),
                        specialHourPeriods: z
                          .array(
                            z.object({
                              startDate: z
                                .string()
                                .describe(
                                  'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                                )
                                .optional(),
                              endDate: z
                                .string()
                                .describe(
                                  'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                                )
                                .optional(),
                              available: z
                                .boolean()
                                .describe(
                                  'Whether the item is available during the exception. Defaults to `true`.'
                                )
                                .optional(),
                              eventName: z
                                .string()
                                .describe(
                                  'Name of the special hour period. In the dashboard, the special hour period is called event.'
                                )
                                .optional()
                                .nullable(),
                            })
                          )
                          .optional(),
                      })
                      .describe('Time periods when the entity is available.')
                      .optional(),
                  })
                  .describe(
                    'Visibility criteria that must be met for the item to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
                  )
                  .optional(),
                dishOptions: z
                  .object({
                    values: z
                      .array(
                        z.intersection(
                          z.object({
                            name: z
                              .string()
                              .describe('Dish option name.')
                              .optional(),
                            type: z
                              .enum([
                                'UNSPECIFIED_DISH_OPTION_TYPE',
                                'SELECTION',
                                'EXTRAS',
                                'DESELECTION',
                              ])
                              .optional(),
                          }),
                          z.xor([
                            z.object({
                              selection: z.never().optional(),
                              extras: z.never().optional(),
                              deselection: z.never().optional(),
                            }),
                            z.object({
                              extras: z.never().optional(),
                              deselection: z.never().optional(),
                              selection: z
                                .object({
                                  defaultChoice: z
                                    .string()
                                    .describe('Item ID of the default choice.')
                                    .optional()
                                    .nullable(),
                                  availableChoices: z
                                    .array(
                                      z.object({
                                        itemId: z
                                          .string()
                                          .describe(
                                            'Item ID of the dish option.'
                                          )
                                          .optional(),
                                        price: z
                                          .object({
                                            value: z
                                              .string()
                                              .describe(
                                                'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                              )
                                              .optional(),
                                            currency: z
                                              .string()
                                              .describe(
                                                'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                              )
                                              .optional(),
                                          })
                                          .describe(
                                            "Dish option price. `0` for free choices that are included in the dish's price."
                                          )
                                          .optional(),
                                      })
                                    )
                                    .optional(),
                                })
                                .describe(
                                  'Item of type `DISH` or `VARIATION` that customers can select. For example a dish size. Customers can choose only a single selection per dish.'
                                ),
                            }),
                            z.object({
                              selection: z.never().optional(),
                              deselection: z.never().optional(),
                              extras: z
                                .object({
                                  defaultChoices: z
                                    .array(z.string())
                                    .optional(),
                                  minChoices: z
                                    .number()
                                    .int()
                                    .describe(
                                      'Minimum number of extras a customer must choose. Defaults to `0`. Must be lower than or equal to the value of `availableChoices`.'
                                    )
                                    .min(0)
                                    .max(2147483647)
                                    .optional()
                                    .nullable(),
                                  maxChoices: z
                                    .number()
                                    .int()
                                    .describe(
                                      'Maximum number of extras a customer can choose. Defaults to the value of `availableChoices`. Must be greater than or equal to the value of `minChoices`.'
                                    )
                                    .min(0)
                                    .max(2147483647)
                                    .optional()
                                    .nullable(),
                                  availableChoices: z
                                    .array(
                                      z.object({
                                        itemId: z
                                          .string()
                                          .describe(
                                            'Item ID of the dish option.'
                                          )
                                          .optional(),
                                        price: z
                                          .object({
                                            value: z
                                              .string()
                                              .describe(
                                                'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                              )
                                              .optional(),
                                            currency: z
                                              .string()
                                              .describe(
                                                'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                              )
                                              .optional(),
                                          })
                                          .describe(
                                            "Dish option price. `0` for free choices that are included in the dish's price."
                                          )
                                          .optional(),
                                      })
                                    )
                                    .optional(),
                                })
                                .describe(
                                  'Item of type `DISH` or `VARIATION` that customers can add to a dish. For example a topping. Customers can add multiple extras per dish.'
                                ),
                            }),
                            z.object({
                              selection: z.never().optional(),
                              extras: z.never().optional(),
                              deselection: z
                                .object({
                                  availableChoices: z
                                    .array(
                                      z.object({
                                        itemId: z
                                          .string()
                                          .describe(
                                            'Item ID of the dish option.'
                                          )
                                          .optional(),
                                        price: z
                                          .object({
                                            value: z
                                              .string()
                                              .describe(
                                                'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                              )
                                              .optional(),
                                            currency: z
                                              .string()
                                              .describe(
                                                'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                              )
                                              .optional(),
                                          })
                                          .describe(
                                            "Dish option price. `0` for free choices that are included in the dish's price."
                                          )
                                          .optional(),
                                      })
                                    )
                                    .optional(),
                                })
                                .describe(
                                  'Item of type `DISH` or `VARIATION` that customers can remove from a dish. For example a specific ingredient. Customers can remove multiple deselections per dish.'
                                ),
                            }),
                          ])
                        )
                      )
                      .optional(),
                  })
                  .describe(
                    'Items customers can choose to modify a dish. Can be an extra, selection, or deselection.'
                  )
                  .optional(),
                acceptSpecialRequest: z
                  .boolean()
                  .describe(
                    'Whether a customer can add a special request when ordering this item. Defaults to `true`.'
                  )
                  .optional()
                  .nullable(),
                type: z
                  .enum(['UNSPECIFIED_ITEM_TYPE', 'DISH', 'VARIATION'])
                  .optional(),
              })
              .describe('Dish to create.')
              .optional(),
          })
        )
        .min(1)
        .max(20)
        .optional(),
      returnFullEntity: z
        .boolean()
        .describe(
          'Whether the full item entity is returned. Defaults to `true`.'
        )
        .optional(),
    })
    .optional(),
});
export const BulkCreateDishesResponse = z.object({
  results: z
    .array(
      z.object({
        entityMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata of the item.')
          .optional(),
        entity: z
          .object({
            _id: z.string().describe('Item ID.').optional().nullable(),
            name: z.string().describe('Item name.').optional().nullable(),
            description: z
              .string()
              .describe('Item description.')
              .optional()
              .nullable(),
            price: z
              .object({
                value: z
                  .string()
                  .describe(
                    'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                  )
                  .optional(),
                currency: z
                  .string()
                  .describe(
                    'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                  )
                  .optional(),
              })
              .describe('Item price.')
              .optional(),
            imageUrl: z
              .string()
              .describe("URL of the item's image file.")
              .optional()
              .nullable(),
            labels: z
              .object({ values: z.array(z.string()).optional() })
              .describe(
                'Item labels. For example spicy, hot, vegan, gluten-free, or organic.'
              )
              .optional(),
            inStock: z
              .boolean()
              .describe('Whether the item is in stock.')
              .optional()
              .nullable(),
            taxRate: z
              .string()
              .describe('Tax rate of the item in percent.')
              .optional()
              .nullable(),
            archived: z
              .boolean()
              .describe(
                "Whether the item is archived. Defaults to `false`. **Note:** Archived items can't be updated."
              )
              .optional()
              .nullable(),
            visibilityCriteria: z
              .object({
                visible: z
                  .boolean()
                  .describe(
                    "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
                  )
                  .optional()
                  .nullable(),
                fulfillmentTypes: z
                  .array(
                    z.enum([
                      'UNSPECIFIED_FULFILLMENT_TYPE',
                      'DELIVERY',
                      'PICKUP_OR_DINE_IN',
                    ])
                  )
                  .min(1)
                  .max(2)
                  .optional(),
                platforms: z
                  .array(
                    z.enum([
                      'UNSPECIFIED_PLATFORM',
                      'SITE',
                      'MOBILE_SITE',
                      'WIX_APP',
                      'CALL_CENTER',
                      'CHAT_BOT',
                    ])
                  )
                  .min(1)
                  .max(5)
                  .optional(),
                availability: z
                  .object({
                    periods: z
                      .array(
                        z.object({
                          openDay: z
                            .enum([
                              'UNDEFINED',
                              'SUN',
                              'MON',
                              'TUE',
                              'WED',
                              'THU',
                              'FRI',
                              'SAT',
                            ])
                            .describe('Day of the week the period starts on.')
                            .optional(),
                          openTime: z
                            .string()
                            .describe(
                              'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                            )
                            .optional(),
                          closeDay: z
                            .enum([
                              'UNDEFINED',
                              'SUN',
                              'MON',
                              'TUE',
                              'WED',
                              'THU',
                              'FRI',
                              'SAT',
                            ])
                            .describe('Day of the week the period ends on.')
                            .optional(),
                          closeTime: z
                            .string()
                            .describe(
                              'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                            )
                            .optional(),
                        })
                      )
                      .optional(),
                    specialHourPeriods: z
                      .array(
                        z.object({
                          startDate: z
                            .string()
                            .describe(
                              'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                            )
                            .optional(),
                          endDate: z
                            .string()
                            .describe(
                              'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                            )
                            .optional(),
                          available: z
                            .boolean()
                            .describe(
                              'Whether the item is available during the exception. Defaults to `true`.'
                            )
                            .optional(),
                          eventName: z
                            .string()
                            .describe(
                              'Name of the special hour period. In the dashboard, the special hour period is called event.'
                            )
                            .optional()
                            .nullable(),
                        })
                      )
                      .optional(),
                  })
                  .describe('Time periods when the entity is available.')
                  .optional(),
              })
              .describe(
                'Visibility criteria that must be met for the item to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
              )
              .optional(),
            dishOptions: z
              .object({
                values: z
                  .array(
                    z.intersection(
                      z.object({
                        name: z
                          .string()
                          .describe('Dish option name.')
                          .optional(),
                        type: z
                          .enum([
                            'UNSPECIFIED_DISH_OPTION_TYPE',
                            'SELECTION',
                            'EXTRAS',
                            'DESELECTION',
                          ])
                          .optional(),
                      }),
                      z.xor([
                        z.object({
                          selection: z.never().optional(),
                          extras: z.never().optional(),
                          deselection: z.never().optional(),
                        }),
                        z.object({
                          extras: z.never().optional(),
                          deselection: z.never().optional(),
                          selection: z
                            .object({
                              defaultChoice: z
                                .string()
                                .describe('Item ID of the default choice.')
                                .optional()
                                .nullable(),
                              availableChoices: z
                                .array(
                                  z.object({
                                    itemId: z
                                      .string()
                                      .describe('Item ID of the dish option.')
                                      .optional(),
                                    price: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe(
                                            'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                          )
                                          .optional(),
                                        currency: z
                                          .string()
                                          .describe(
                                            'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                          )
                                          .optional(),
                                      })
                                      .describe(
                                        "Dish option price. `0` for free choices that are included in the dish's price."
                                      )
                                      .optional(),
                                  })
                                )
                                .optional(),
                            })
                            .describe(
                              'Item of type `DISH` or `VARIATION` that customers can select. For example a dish size. Customers can choose only a single selection per dish.'
                            ),
                        }),
                        z.object({
                          selection: z.never().optional(),
                          deselection: z.never().optional(),
                          extras: z
                            .object({
                              defaultChoices: z.array(z.string()).optional(),
                              minChoices: z
                                .number()
                                .int()
                                .describe(
                                  'Minimum number of extras a customer must choose. Defaults to `0`. Must be lower than or equal to the value of `availableChoices`.'
                                )
                                .min(0)
                                .max(2147483647)
                                .optional()
                                .nullable(),
                              maxChoices: z
                                .number()
                                .int()
                                .describe(
                                  'Maximum number of extras a customer can choose. Defaults to the value of `availableChoices`. Must be greater than or equal to the value of `minChoices`.'
                                )
                                .min(0)
                                .max(2147483647)
                                .optional()
                                .nullable(),
                              availableChoices: z
                                .array(
                                  z.object({
                                    itemId: z
                                      .string()
                                      .describe('Item ID of the dish option.')
                                      .optional(),
                                    price: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe(
                                            'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                          )
                                          .optional(),
                                        currency: z
                                          .string()
                                          .describe(
                                            'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                          )
                                          .optional(),
                                      })
                                      .describe(
                                        "Dish option price. `0` for free choices that are included in the dish's price."
                                      )
                                      .optional(),
                                  })
                                )
                                .optional(),
                            })
                            .describe(
                              'Item of type `DISH` or `VARIATION` that customers can add to a dish. For example a topping. Customers can add multiple extras per dish.'
                            ),
                        }),
                        z.object({
                          selection: z.never().optional(),
                          extras: z.never().optional(),
                          deselection: z
                            .object({
                              availableChoices: z
                                .array(
                                  z.object({
                                    itemId: z
                                      .string()
                                      .describe('Item ID of the dish option.')
                                      .optional(),
                                    price: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe(
                                            'Monetary amount in decimal string format. For example, `3.99`, `6`, and `10.5` are all accepted values.'
                                          )
                                          .optional(),
                                        currency: z
                                          .string()
                                          .describe(
                                            'Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.'
                                          )
                                          .optional(),
                                      })
                                      .describe(
                                        "Dish option price. `0` for free choices that are included in the dish's price."
                                      )
                                      .optional(),
                                  })
                                )
                                .optional(),
                            })
                            .describe(
                              'Item of type `DISH` or `VARIATION` that customers can remove from a dish. For example a specific ingredient. Customers can remove multiple deselections per dish.'
                            ),
                        }),
                      ])
                    )
                  )
                  .optional(),
              })
              .describe(
                'Items customers can choose to modify a dish. Can be an extra, selection, or deselection.'
              )
              .optional(),
            acceptSpecialRequest: z
              .boolean()
              .describe(
                'Whether a customer can add a special request when ordering this item. Defaults to `true`.'
              )
              .optional()
              .nullable(),
            type: z
              .enum(['UNSPECIFIED_ITEM_TYPE', 'DISH', 'VARIATION'])
              .describe('Item type.')
              .optional(),
          })
          .describe('Item.')
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk Create Dishes metadata.')
    .optional(),
});
export const BulkCreateMenusRequest = z.object({
  catalogId: z.string().describe('ID of the catalog the menus will belong to.'),
  options: z
    .object({
      menus: z
        .array(
          z.object({
            _id: z.string().describe('Menu ID.').optional().nullable(),
            name: z.string().describe('Menu name.').optional().nullable(),
            description: z
              .string()
              .describe('Menu description.')
              .optional()
              .nullable(),
            imageUrl: z
              .string()
              .describe("URL of the menu's image file.")
              .optional()
              .nullable(),
            visibilityCriteria: z
              .object({
                visible: z
                  .boolean()
                  .describe(
                    "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
                  )
                  .optional()
                  .nullable(),
                fulfillmentTypes: z
                  .array(
                    z.enum([
                      'UNSPECIFIED_FULFILLMENT_TYPE',
                      'DELIVERY',
                      'PICKUP_OR_DINE_IN',
                    ])
                  )
                  .min(1)
                  .max(2)
                  .optional(),
                platforms: z
                  .array(
                    z.enum([
                      'UNSPECIFIED_PLATFORM',
                      'SITE',
                      'MOBILE_SITE',
                      'WIX_APP',
                      'CALL_CENTER',
                      'CHAT_BOT',
                    ])
                  )
                  .min(1)
                  .max(5)
                  .optional(),
                availability: z
                  .object({
                    periods: z
                      .array(
                        z.object({
                          openDay: z
                            .enum([
                              'UNDEFINED',
                              'SUN',
                              'MON',
                              'TUE',
                              'WED',
                              'THU',
                              'FRI',
                              'SAT',
                            ])
                            .optional(),
                          openTime: z
                            .string()
                            .describe(
                              'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                            )
                            .optional(),
                          closeDay: z
                            .enum([
                              'UNDEFINED',
                              'SUN',
                              'MON',
                              'TUE',
                              'WED',
                              'THU',
                              'FRI',
                              'SAT',
                            ])
                            .optional(),
                          closeTime: z
                            .string()
                            .describe(
                              'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                            )
                            .optional(),
                        })
                      )
                      .optional(),
                    specialHourPeriods: z
                      .array(
                        z.object({
                          startDate: z
                            .string()
                            .describe(
                              'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                            )
                            .optional(),
                          endDate: z
                            .string()
                            .describe(
                              'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                            )
                            .optional(),
                          available: z
                            .boolean()
                            .describe(
                              'Whether the item is available during the exception. Defaults to `true`.'
                            )
                            .optional(),
                          eventName: z
                            .string()
                            .describe(
                              'Name of the special hour period. In the dashboard, the special hour period is called event.'
                            )
                            .optional()
                            .nullable(),
                        })
                      )
                      .optional(),
                  })
                  .describe('Time periods when the entity is available.')
                  .optional(),
              })
              .describe(
                'Visibility criteria that must be met for the menu to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
              )
              .optional(),
            sectionIds: z
              .object({ values: z.array(z.string()).optional() })
              .describe('IDs of the sections that are included in the menu.')
              .optional(),
            archived: z
              .boolean()
              .describe(
                "Whether the menu is archived. Defaults to `false`. **Note:** Archived menus can't be updated."
              )
              .optional()
              .nullable(),
          })
        )
        .min(1)
        .max(20)
        .optional(),
      returnFullEntity: z
        .boolean()
        .describe(
          'Whether the full menu entity is returned. Defaults to `true`.'
        )
        .optional(),
    })
    .optional(),
});
export const BulkCreateMenusResponse = z.object({
  results: z
    .array(
      z.object({
        entityMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Menu metadata.')
          .optional(),
        entity: z
          .object({
            _id: z.string().describe('Menu ID.').optional().nullable(),
            name: z.string().describe('Menu name.').optional().nullable(),
            description: z
              .string()
              .describe('Menu description.')
              .optional()
              .nullable(),
            imageUrl: z
              .string()
              .describe("URL of the menu's image file.")
              .optional()
              .nullable(),
            visibilityCriteria: z
              .object({
                visible: z
                  .boolean()
                  .describe(
                    "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
                  )
                  .optional()
                  .nullable(),
                fulfillmentTypes: z
                  .array(
                    z.enum([
                      'UNSPECIFIED_FULFILLMENT_TYPE',
                      'DELIVERY',
                      'PICKUP_OR_DINE_IN',
                    ])
                  )
                  .min(1)
                  .max(2)
                  .optional(),
                platforms: z
                  .array(
                    z.enum([
                      'UNSPECIFIED_PLATFORM',
                      'SITE',
                      'MOBILE_SITE',
                      'WIX_APP',
                      'CALL_CENTER',
                      'CHAT_BOT',
                    ])
                  )
                  .min(1)
                  .max(5)
                  .optional(),
                availability: z
                  .object({
                    periods: z
                      .array(
                        z.object({
                          openDay: z
                            .enum([
                              'UNDEFINED',
                              'SUN',
                              'MON',
                              'TUE',
                              'WED',
                              'THU',
                              'FRI',
                              'SAT',
                            ])
                            .describe('Day of the week the period starts on.')
                            .optional(),
                          openTime: z
                            .string()
                            .describe(
                              'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                            )
                            .optional(),
                          closeDay: z
                            .enum([
                              'UNDEFINED',
                              'SUN',
                              'MON',
                              'TUE',
                              'WED',
                              'THU',
                              'FRI',
                              'SAT',
                            ])
                            .describe('Day of the week the period ends on.')
                            .optional(),
                          closeTime: z
                            .string()
                            .describe(
                              'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                            )
                            .optional(),
                        })
                      )
                      .optional(),
                    specialHourPeriods: z
                      .array(
                        z.object({
                          startDate: z
                            .string()
                            .describe(
                              'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                            )
                            .optional(),
                          endDate: z
                            .string()
                            .describe(
                              'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                            )
                            .optional(),
                          available: z
                            .boolean()
                            .describe(
                              'Whether the item is available during the exception. Defaults to `true`.'
                            )
                            .optional(),
                          eventName: z
                            .string()
                            .describe(
                              'Name of the special hour period. In the dashboard, the special hour period is called event.'
                            )
                            .optional()
                            .nullable(),
                        })
                      )
                      .optional(),
                  })
                  .describe('Time periods when the entity is available.')
                  .optional(),
              })
              .describe(
                'Visibility criteria that must be met for the menu to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
              )
              .optional(),
            sectionIds: z
              .object({ values: z.array(z.string()).optional() })
              .describe('IDs of the sections that are included in the menu.')
              .optional(),
            archived: z
              .boolean()
              .describe(
                "Whether the menu is archived. Defaults to `false`. **Note:** Archived menus can't be updated."
              )
              .optional()
              .nullable(),
          })
          .describe('Menu.')
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk Create Menus metadata.')
    .optional(),
});
export const BulkUpdateMenusRequest = z.object({
  catalogId: z.string().describe('ID of the catalog the menus belong to.'),
  options: z
    .object({
      menus: z
        .array(
          z.object({
            _id: z.string().describe('Menu ID.').optional().nullable(),
            name: z.string().describe('Menu name.').optional().nullable(),
            description: z
              .string()
              .describe('Menu description.')
              .optional()
              .nullable(),
            imageUrl: z
              .string()
              .describe("URL of the menu's image file.")
              .optional()
              .nullable(),
            visibilityCriteria: z
              .object({
                visible: z
                  .boolean()
                  .describe(
                    "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
                  )
                  .optional()
                  .nullable(),
                fulfillmentTypes: z
                  .array(
                    z.enum([
                      'UNSPECIFIED_FULFILLMENT_TYPE',
                      'DELIVERY',
                      'PICKUP_OR_DINE_IN',
                    ])
                  )
                  .min(1)
                  .max(2)
                  .optional(),
                platforms: z
                  .array(
                    z.enum([
                      'UNSPECIFIED_PLATFORM',
                      'SITE',
                      'MOBILE_SITE',
                      'WIX_APP',
                      'CALL_CENTER',
                      'CHAT_BOT',
                    ])
                  )
                  .min(1)
                  .max(5)
                  .optional(),
                availability: z
                  .object({
                    periods: z
                      .array(
                        z.object({
                          openDay: z
                            .enum([
                              'UNDEFINED',
                              'SUN',
                              'MON',
                              'TUE',
                              'WED',
                              'THU',
                              'FRI',
                              'SAT',
                            ])
                            .optional(),
                          openTime: z
                            .string()
                            .describe(
                              'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                            )
                            .optional(),
                          closeDay: z
                            .enum([
                              'UNDEFINED',
                              'SUN',
                              'MON',
                              'TUE',
                              'WED',
                              'THU',
                              'FRI',
                              'SAT',
                            ])
                            .optional(),
                          closeTime: z
                            .string()
                            .describe(
                              'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                            )
                            .optional(),
                        })
                      )
                      .optional(),
                    specialHourPeriods: z
                      .array(
                        z.object({
                          startDate: z
                            .string()
                            .describe(
                              'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                            )
                            .optional(),
                          endDate: z
                            .string()
                            .describe(
                              'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                            )
                            .optional(),
                          available: z
                            .boolean()
                            .describe(
                              'Whether the item is available during the exception. Defaults to `true`.'
                            )
                            .optional(),
                          eventName: z
                            .string()
                            .describe(
                              'Name of the special hour period. In the dashboard, the special hour period is called event.'
                            )
                            .optional()
                            .nullable(),
                        })
                      )
                      .optional(),
                  })
                  .describe('Time periods when the entity is available.')
                  .optional(),
              })
              .describe(
                'Visibility criteria that must be met for the menu to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
              )
              .optional(),
            sectionIds: z
              .object({ values: z.array(z.string()).optional() })
              .describe('IDs of the sections that are included in the menu.')
              .optional(),
            archived: z
              .boolean()
              .describe(
                "Whether the menu is archived. Defaults to `false`. **Note:** Archived menus can't be updated."
              )
              .optional()
              .nullable(),
          })
        )
        .min(1)
        .max(20)
        .optional(),
      returnFullEntity: z
        .boolean()
        .describe(
          'Whether the full menu entity is returned. Defaults to `true`.'
        )
        .optional(),
    })
    .optional(),
});
export const BulkUpdateMenusResponse = z.object({
  results: z
    .array(
      z.object({
        entityMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Menu metadata.')
          .optional(),
        entity: z
          .object({
            _id: z.string().describe('Menu ID.').optional().nullable(),
            name: z.string().describe('Menu name.').optional().nullable(),
            description: z
              .string()
              .describe('Menu description.')
              .optional()
              .nullable(),
            imageUrl: z
              .string()
              .describe("URL of the menu's image file.")
              .optional()
              .nullable(),
            visibilityCriteria: z
              .object({
                visible: z
                  .boolean()
                  .describe(
                    "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
                  )
                  .optional()
                  .nullable(),
                fulfillmentTypes: z
                  .array(
                    z.enum([
                      'UNSPECIFIED_FULFILLMENT_TYPE',
                      'DELIVERY',
                      'PICKUP_OR_DINE_IN',
                    ])
                  )
                  .min(1)
                  .max(2)
                  .optional(),
                platforms: z
                  .array(
                    z.enum([
                      'UNSPECIFIED_PLATFORM',
                      'SITE',
                      'MOBILE_SITE',
                      'WIX_APP',
                      'CALL_CENTER',
                      'CHAT_BOT',
                    ])
                  )
                  .min(1)
                  .max(5)
                  .optional(),
                availability: z
                  .object({
                    periods: z
                      .array(
                        z.object({
                          openDay: z
                            .enum([
                              'UNDEFINED',
                              'SUN',
                              'MON',
                              'TUE',
                              'WED',
                              'THU',
                              'FRI',
                              'SAT',
                            ])
                            .describe('Day of the week the period starts on.')
                            .optional(),
                          openTime: z
                            .string()
                            .describe(
                              'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                            )
                            .optional(),
                          closeDay: z
                            .enum([
                              'UNDEFINED',
                              'SUN',
                              'MON',
                              'TUE',
                              'WED',
                              'THU',
                              'FRI',
                              'SAT',
                            ])
                            .describe('Day of the week the period ends on.')
                            .optional(),
                          closeTime: z
                            .string()
                            .describe(
                              'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                            )
                            .optional(),
                        })
                      )
                      .optional(),
                    specialHourPeriods: z
                      .array(
                        z.object({
                          startDate: z
                            .string()
                            .describe(
                              'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                            )
                            .optional(),
                          endDate: z
                            .string()
                            .describe(
                              'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                            )
                            .optional(),
                          available: z
                            .boolean()
                            .describe(
                              'Whether the item is available during the exception. Defaults to `true`.'
                            )
                            .optional(),
                          eventName: z
                            .string()
                            .describe(
                              'Name of the special hour period. In the dashboard, the special hour period is called event.'
                            )
                            .optional()
                            .nullable(),
                        })
                      )
                      .optional(),
                  })
                  .describe('Time periods when the entity is available.')
                  .optional(),
              })
              .describe(
                'Visibility criteria that must be met for the menu to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
              )
              .optional(),
            sectionIds: z
              .object({ values: z.array(z.string()).optional() })
              .describe('IDs of the sections that are included in the menu.')
              .optional(),
            archived: z
              .boolean()
              .describe(
                "Whether the menu is archived. Defaults to `false`. **Note:** Archived menus can't be updated."
              )
              .optional()
              .nullable(),
          })
          .describe('Menu.')
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk Update Menus metadata.')
    .optional(),
});
export const BulkCreateSectionsRequest = z.object({
  catalogId: z
    .string()
    .describe('ID of the catalog the sections will belong to.'),
  options: z
    .object({
      createSectionRequests: z
        .array(
          z.object({
            menuId: z
              .string()
              .describe('ID of the menu the section will belong to.')
              .optional(),
            section: z
              .object({
                _id: z.string().describe('Section ID.').optional().nullable(),
                menuId: z
                  .string()
                  .describe('ID of the menu the section belongs to.')
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Section name.')
                  .optional()
                  .nullable(),
                description: z
                  .string()
                  .describe('Section description.')
                  .optional()
                  .nullable(),
                imageUrl: z
                  .string()
                  .describe("URL of the section's image file.")
                  .optional()
                  .nullable(),
                itemIds: z
                  .object({ values: z.array(z.string()).optional() })
                  .describe('IDs of the items that belong to the section.')
                  .optional(),
                visibilityCriteria: z
                  .object({
                    visible: z
                      .boolean()
                      .describe(
                        "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
                      )
                      .optional()
                      .nullable(),
                    fulfillmentTypes: z
                      .array(
                        z.enum([
                          'UNSPECIFIED_FULFILLMENT_TYPE',
                          'DELIVERY',
                          'PICKUP_OR_DINE_IN',
                        ])
                      )
                      .min(1)
                      .max(2)
                      .optional(),
                    platforms: z
                      .array(
                        z.enum([
                          'UNSPECIFIED_PLATFORM',
                          'SITE',
                          'MOBILE_SITE',
                          'WIX_APP',
                          'CALL_CENTER',
                          'CHAT_BOT',
                        ])
                      )
                      .min(1)
                      .max(5)
                      .optional(),
                    availability: z
                      .object({
                        periods: z
                          .array(
                            z.object({
                              openDay: z
                                .enum([
                                  'UNDEFINED',
                                  'SUN',
                                  'MON',
                                  'TUE',
                                  'WED',
                                  'THU',
                                  'FRI',
                                  'SAT',
                                ])
                                .optional(),
                              openTime: z
                                .string()
                                .describe(
                                  'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                                )
                                .optional(),
                              closeDay: z
                                .enum([
                                  'UNDEFINED',
                                  'SUN',
                                  'MON',
                                  'TUE',
                                  'WED',
                                  'THU',
                                  'FRI',
                                  'SAT',
                                ])
                                .optional(),
                              closeTime: z
                                .string()
                                .describe(
                                  'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                                )
                                .optional(),
                            })
                          )
                          .optional(),
                        specialHourPeriods: z
                          .array(
                            z.object({
                              startDate: z
                                .string()
                                .describe(
                                  'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                                )
                                .optional(),
                              endDate: z
                                .string()
                                .describe(
                                  'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                                )
                                .optional(),
                              available: z
                                .boolean()
                                .describe(
                                  'Whether the item is available during the exception. Defaults to `true`.'
                                )
                                .optional(),
                              eventName: z
                                .string()
                                .describe(
                                  'Name of the special hour period. In the dashboard, the special hour period is called event.'
                                )
                                .optional()
                                .nullable(),
                            })
                          )
                          .optional(),
                      })
                      .describe('Time periods when the entity is available.')
                      .optional(),
                  })
                  .describe(
                    'Visibility criteria that must be met for the section to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
                  )
                  .optional(),
                archived: z
                  .boolean()
                  .describe(
                    "Whether the section is archived. Defaults to `false`. **Note:** Archived sections can't be updated."
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Section to create.')
              .optional(),
          })
        )
        .min(1)
        .max(20)
        .optional(),
      returnFullEntity: z
        .boolean()
        .describe(
          'Whether the full section entity is returned. Defaults to `true`.'
        )
        .optional(),
    })
    .optional(),
});
export const BulkCreateSectionsResponse = z.object({
  results: z
    .array(
      z.object({
        entityMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Section metadata.')
          .optional(),
        entity: z
          .object({
            _id: z.string().describe('Section ID.').optional().nullable(),
            menuId: z
              .string()
              .describe('ID of the menu the section belongs to.')
              .optional()
              .nullable(),
            name: z.string().describe('Section name.').optional().nullable(),
            description: z
              .string()
              .describe('Section description.')
              .optional()
              .nullable(),
            imageUrl: z
              .string()
              .describe("URL of the section's image file.")
              .optional()
              .nullable(),
            itemIds: z
              .object({ values: z.array(z.string()).optional() })
              .describe('IDs of the items that belong to the section.')
              .optional(),
            visibilityCriteria: z
              .object({
                visible: z
                  .boolean()
                  .describe(
                    "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
                  )
                  .optional()
                  .nullable(),
                fulfillmentTypes: z
                  .array(
                    z.enum([
                      'UNSPECIFIED_FULFILLMENT_TYPE',
                      'DELIVERY',
                      'PICKUP_OR_DINE_IN',
                    ])
                  )
                  .min(1)
                  .max(2)
                  .optional(),
                platforms: z
                  .array(
                    z.enum([
                      'UNSPECIFIED_PLATFORM',
                      'SITE',
                      'MOBILE_SITE',
                      'WIX_APP',
                      'CALL_CENTER',
                      'CHAT_BOT',
                    ])
                  )
                  .min(1)
                  .max(5)
                  .optional(),
                availability: z
                  .object({
                    periods: z
                      .array(
                        z.object({
                          openDay: z
                            .enum([
                              'UNDEFINED',
                              'SUN',
                              'MON',
                              'TUE',
                              'WED',
                              'THU',
                              'FRI',
                              'SAT',
                            ])
                            .describe('Day of the week the period starts on.')
                            .optional(),
                          openTime: z
                            .string()
                            .describe(
                              'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                            )
                            .optional(),
                          closeDay: z
                            .enum([
                              'UNDEFINED',
                              'SUN',
                              'MON',
                              'TUE',
                              'WED',
                              'THU',
                              'FRI',
                              'SAT',
                            ])
                            .describe('Day of the week the period ends on.')
                            .optional(),
                          closeTime: z
                            .string()
                            .describe(
                              'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                            )
                            .optional(),
                        })
                      )
                      .optional(),
                    specialHourPeriods: z
                      .array(
                        z.object({
                          startDate: z
                            .string()
                            .describe(
                              'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                            )
                            .optional(),
                          endDate: z
                            .string()
                            .describe(
                              'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                            )
                            .optional(),
                          available: z
                            .boolean()
                            .describe(
                              'Whether the item is available during the exception. Defaults to `true`.'
                            )
                            .optional(),
                          eventName: z
                            .string()
                            .describe(
                              'Name of the special hour period. In the dashboard, the special hour period is called event.'
                            )
                            .optional()
                            .nullable(),
                        })
                      )
                      .optional(),
                  })
                  .describe('Time periods when the entity is available.')
                  .optional(),
              })
              .describe(
                'Visibility criteria that must be met for the section to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
              )
              .optional(),
            archived: z
              .boolean()
              .describe(
                "Whether the section is archived. Defaults to `false`. **Note:** Archived sections can't be updated."
              )
              .optional()
              .nullable(),
          })
          .describe('Section.')
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk Create Sections metadata.')
    .optional(),
});
export const BulkUpdateSectionsRequest = z.object({
  catalogId: z.string().describe('ID of the catalog the sections belong to.'),
  options: z
    .object({
      updateSectionsRequests: z
        .array(
          z.object({
            menuId: z
              .string()
              .describe('ID of the menu the section will belong to.')
              .optional(),
            section: z
              .object({
                _id: z.string().describe('Section ID.').optional().nullable(),
                menuId: z
                  .string()
                  .describe('ID of the menu the section belongs to.')
                  .optional()
                  .nullable(),
                name: z
                  .string()
                  .describe('Section name.')
                  .optional()
                  .nullable(),
                description: z
                  .string()
                  .describe('Section description.')
                  .optional()
                  .nullable(),
                imageUrl: z
                  .string()
                  .describe("URL of the section's image file.")
                  .optional()
                  .nullable(),
                itemIds: z
                  .object({ values: z.array(z.string()).optional() })
                  .describe('IDs of the items that belong to the section.')
                  .optional(),
                visibilityCriteria: z
                  .object({
                    visible: z
                      .boolean()
                      .describe(
                        "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
                      )
                      .optional()
                      .nullable(),
                    fulfillmentTypes: z
                      .array(
                        z.enum([
                          'UNSPECIFIED_FULFILLMENT_TYPE',
                          'DELIVERY',
                          'PICKUP_OR_DINE_IN',
                        ])
                      )
                      .min(1)
                      .max(2)
                      .optional(),
                    platforms: z
                      .array(
                        z.enum([
                          'UNSPECIFIED_PLATFORM',
                          'SITE',
                          'MOBILE_SITE',
                          'WIX_APP',
                          'CALL_CENTER',
                          'CHAT_BOT',
                        ])
                      )
                      .min(1)
                      .max(5)
                      .optional(),
                    availability: z
                      .object({
                        periods: z
                          .array(
                            z.object({
                              openDay: z
                                .enum([
                                  'UNDEFINED',
                                  'SUN',
                                  'MON',
                                  'TUE',
                                  'WED',
                                  'THU',
                                  'FRI',
                                  'SAT',
                                ])
                                .optional(),
                              openTime: z
                                .string()
                                .describe(
                                  'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                                )
                                .optional(),
                              closeDay: z
                                .enum([
                                  'UNDEFINED',
                                  'SUN',
                                  'MON',
                                  'TUE',
                                  'WED',
                                  'THU',
                                  'FRI',
                                  'SAT',
                                ])
                                .optional(),
                              closeTime: z
                                .string()
                                .describe(
                                  'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                                )
                                .optional(),
                            })
                          )
                          .optional(),
                        specialHourPeriods: z
                          .array(
                            z.object({
                              startDate: z
                                .string()
                                .describe(
                                  'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                                )
                                .optional(),
                              endDate: z
                                .string()
                                .describe(
                                  'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                                )
                                .optional(),
                              available: z
                                .boolean()
                                .describe(
                                  'Whether the item is available during the exception. Defaults to `true`.'
                                )
                                .optional(),
                              eventName: z
                                .string()
                                .describe(
                                  'Name of the special hour period. In the dashboard, the special hour period is called event.'
                                )
                                .optional()
                                .nullable(),
                            })
                          )
                          .optional(),
                      })
                      .describe('Time periods when the entity is available.')
                      .optional(),
                  })
                  .describe(
                    'Visibility criteria that must be met for the section to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
                  )
                  .optional(),
                archived: z
                  .boolean()
                  .describe(
                    "Whether the section is archived. Defaults to `false`. **Note:** Archived sections can't be updated."
                  )
                  .optional()
                  .nullable(),
              })
              .describe('Section to update.')
              .optional(),
          })
        )
        .min(1)
        .max(20)
        .optional(),
      returnFullEntity: z
        .boolean()
        .describe(
          'Whether the full section entity is returned. Defaults to `true`.'
        )
        .optional(),
    })
    .optional(),
});
export const BulkUpdateSectionsResponse = z.object({
  results: z
    .array(
      z.object({
        entityMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Section metadata.')
          .optional(),
        entity: z
          .object({
            _id: z.string().describe('Section ID.').optional().nullable(),
            menuId: z
              .string()
              .describe('ID of the menu the section belongs to.')
              .optional()
              .nullable(),
            name: z.string().describe('Section name.').optional().nullable(),
            description: z
              .string()
              .describe('Section description.')
              .optional()
              .nullable(),
            imageUrl: z
              .string()
              .describe("URL of the section's image file.")
              .optional()
              .nullable(),
            itemIds: z
              .object({ values: z.array(z.string()).optional() })
              .describe('IDs of the items that belong to the section.')
              .optional(),
            visibilityCriteria: z
              .object({
                visible: z
                  .boolean()
                  .describe(
                    "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
                  )
                  .optional()
                  .nullable(),
                fulfillmentTypes: z
                  .array(
                    z.enum([
                      'UNSPECIFIED_FULFILLMENT_TYPE',
                      'DELIVERY',
                      'PICKUP_OR_DINE_IN',
                    ])
                  )
                  .min(1)
                  .max(2)
                  .optional(),
                platforms: z
                  .array(
                    z.enum([
                      'UNSPECIFIED_PLATFORM',
                      'SITE',
                      'MOBILE_SITE',
                      'WIX_APP',
                      'CALL_CENTER',
                      'CHAT_BOT',
                    ])
                  )
                  .min(1)
                  .max(5)
                  .optional(),
                availability: z
                  .object({
                    periods: z
                      .array(
                        z.object({
                          openDay: z
                            .enum([
                              'UNDEFINED',
                              'SUN',
                              'MON',
                              'TUE',
                              'WED',
                              'THU',
                              'FRI',
                              'SAT',
                            ])
                            .describe('Day of the week the period starts on.')
                            .optional(),
                          openTime: z
                            .string()
                            .describe(
                              'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                            )
                            .optional(),
                          closeDay: z
                            .enum([
                              'UNDEFINED',
                              'SUN',
                              'MON',
                              'TUE',
                              'WED',
                              'THU',
                              'FRI',
                              'SAT',
                            ])
                            .describe('Day of the week the period ends on.')
                            .optional(),
                          closeTime: z
                            .string()
                            .describe(
                              'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                            )
                            .optional(),
                        })
                      )
                      .optional(),
                    specialHourPeriods: z
                      .array(
                        z.object({
                          startDate: z
                            .string()
                            .describe(
                              'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                            )
                            .optional(),
                          endDate: z
                            .string()
                            .describe(
                              'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                            )
                            .optional(),
                          available: z
                            .boolean()
                            .describe(
                              'Whether the item is available during the exception. Defaults to `true`.'
                            )
                            .optional(),
                          eventName: z
                            .string()
                            .describe(
                              'Name of the special hour period. In the dashboard, the special hour period is called event.'
                            )
                            .optional()
                            .nullable(),
                        })
                      )
                      .optional(),
                  })
                  .describe('Time periods when the entity is available.')
                  .optional(),
              })
              .describe(
                'Visibility criteria that must be met for the section to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
              )
              .optional(),
            archived: z
              .boolean()
              .describe(
                "Whether the section is archived. Defaults to `false`. **Note:** Archived sections can't be updated."
              )
              .optional()
              .nullable(),
          })
          .describe('Section.')
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk Update Sections metadata.')
    .optional(),
});
export const BulkArchiveMenusRequest = z.object({
  catalogId: z.string().describe('ID of the catalog the menus belong to.'),
  options: z
    .object({
      ids: z.array(z.string()).min(1).max(20).optional(),
      returnFullEntity: z
        .boolean()
        .describe(
          'Whether the full menu entity is returned. Defaults to `true`.'
        )
        .optional(),
    })
    .optional(),
});
export const BulkArchiveMenusResponse = z.object({
  results: z
    .array(
      z.object({
        entityMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Menu metadata.')
          .optional(),
        entity: z
          .object({
            _id: z.string().describe('Menu ID.').optional().nullable(),
            name: z.string().describe('Menu name.').optional().nullable(),
            description: z
              .string()
              .describe('Menu description.')
              .optional()
              .nullable(),
            imageUrl: z
              .string()
              .describe("URL of the menu's image file.")
              .optional()
              .nullable(),
            visibilityCriteria: z
              .object({
                visible: z
                  .boolean()
                  .describe(
                    "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
                  )
                  .optional()
                  .nullable(),
                fulfillmentTypes: z
                  .array(
                    z.enum([
                      'UNSPECIFIED_FULFILLMENT_TYPE',
                      'DELIVERY',
                      'PICKUP_OR_DINE_IN',
                    ])
                  )
                  .min(1)
                  .max(2)
                  .optional(),
                platforms: z
                  .array(
                    z.enum([
                      'UNSPECIFIED_PLATFORM',
                      'SITE',
                      'MOBILE_SITE',
                      'WIX_APP',
                      'CALL_CENTER',
                      'CHAT_BOT',
                    ])
                  )
                  .min(1)
                  .max(5)
                  .optional(),
                availability: z
                  .object({
                    periods: z
                      .array(
                        z.object({
                          openDay: z
                            .enum([
                              'UNDEFINED',
                              'SUN',
                              'MON',
                              'TUE',
                              'WED',
                              'THU',
                              'FRI',
                              'SAT',
                            ])
                            .describe('Day of the week the period starts on.')
                            .optional(),
                          openTime: z
                            .string()
                            .describe(
                              'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                            )
                            .optional(),
                          closeDay: z
                            .enum([
                              'UNDEFINED',
                              'SUN',
                              'MON',
                              'TUE',
                              'WED',
                              'THU',
                              'FRI',
                              'SAT',
                            ])
                            .describe('Day of the week the period ends on.')
                            .optional(),
                          closeTime: z
                            .string()
                            .describe(
                              'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                            )
                            .optional(),
                        })
                      )
                      .optional(),
                    specialHourPeriods: z
                      .array(
                        z.object({
                          startDate: z
                            .string()
                            .describe(
                              'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                            )
                            .optional(),
                          endDate: z
                            .string()
                            .describe(
                              'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                            )
                            .optional(),
                          available: z
                            .boolean()
                            .describe(
                              'Whether the item is available during the exception. Defaults to `true`.'
                            )
                            .optional(),
                          eventName: z
                            .string()
                            .describe(
                              'Name of the special hour period. In the dashboard, the special hour period is called event.'
                            )
                            .optional()
                            .nullable(),
                        })
                      )
                      .optional(),
                  })
                  .describe('Time periods when the entity is available.')
                  .optional(),
              })
              .describe(
                'Visibility criteria that must be met for the menu to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
              )
              .optional(),
            sectionIds: z
              .object({ values: z.array(z.string()).optional() })
              .describe('IDs of the sections that are included in the menu.')
              .optional(),
            archived: z
              .boolean()
              .describe(
                "Whether the menu is archived. Defaults to `false`. **Note:** Archived menus can't be updated."
              )
              .optional()
              .nullable(),
          })
          .describe('Menu.')
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk Archive Menus metadata.')
    .optional(),
});
export const BulkUnarchiveMenusRequest = z.object({
  catalogId: z.string().describe('ID of the catalog the menus belong to.'),
  options: z
    .object({
      ids: z.array(z.string()).min(1).max(20).optional(),
      returnFullEntity: z
        .boolean()
        .describe(
          'Whether the full menu entity is returned. Defaults to `true`.'
        )
        .optional(),
    })
    .optional(),
});
export const BulkUnarchiveMenusResponse = z.object({
  results: z
    .array(
      z.object({
        entityMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Menu metadata.')
          .optional(),
        entity: z
          .object({
            _id: z.string().describe('Menu ID.').optional().nullable(),
            name: z.string().describe('Menu name.').optional().nullable(),
            description: z
              .string()
              .describe('Menu description.')
              .optional()
              .nullable(),
            imageUrl: z
              .string()
              .describe("URL of the menu's image file.")
              .optional()
              .nullable(),
            visibilityCriteria: z
              .object({
                visible: z
                  .boolean()
                  .describe(
                    "Whether the entity appears in the live site. Defaults to `true`.\nIf `false`, the entity isn't rendered in the live site, even if each visibility criterion is fulfilled."
                  )
                  .optional()
                  .nullable(),
                fulfillmentTypes: z
                  .array(
                    z.enum([
                      'UNSPECIFIED_FULFILLMENT_TYPE',
                      'DELIVERY',
                      'PICKUP_OR_DINE_IN',
                    ])
                  )
                  .min(1)
                  .max(2)
                  .optional(),
                platforms: z
                  .array(
                    z.enum([
                      'UNSPECIFIED_PLATFORM',
                      'SITE',
                      'MOBILE_SITE',
                      'WIX_APP',
                      'CALL_CENTER',
                      'CHAT_BOT',
                    ])
                  )
                  .min(1)
                  .max(5)
                  .optional(),
                availability: z
                  .object({
                    periods: z
                      .array(
                        z.object({
                          openDay: z
                            .enum([
                              'UNDEFINED',
                              'SUN',
                              'MON',
                              'TUE',
                              'WED',
                              'THU',
                              'FRI',
                              'SAT',
                            ])
                            .describe('Day of the week the period starts on.')
                            .optional(),
                          openTime: z
                            .string()
                            .describe(
                              'Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.'
                            )
                            .optional(),
                          closeDay: z
                            .enum([
                              'UNDEFINED',
                              'SUN',
                              'MON',
                              'TUE',
                              'WED',
                              'THU',
                              'FRI',
                              'SAT',
                            ])
                            .describe('Day of the week the period ends on.')
                            .optional(),
                          closeTime: z
                            .string()
                            .describe(
                              'Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents\nmidnight at the end of the specified day.\n__Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.'
                            )
                            .optional(),
                        })
                      )
                      .optional(),
                    specialHourPeriods: z
                      .array(
                        z.object({
                          startDate: z
                            .string()
                            .describe(
                              'Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                            )
                            .optional(),
                          endDate: z
                            .string()
                            .describe(
                              'End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).'
                            )
                            .optional(),
                          available: z
                            .boolean()
                            .describe(
                              'Whether the item is available during the exception. Defaults to `true`.'
                            )
                            .optional(),
                          eventName: z
                            .string()
                            .describe(
                              'Name of the special hour period. In the dashboard, the special hour period is called event.'
                            )
                            .optional()
                            .nullable(),
                        })
                      )
                      .optional(),
                  })
                  .describe('Time periods when the entity is available.')
                  .optional(),
              })
              .describe(
                'Visibility criteria that must be met for the menu to appear in the live site.\nIn case of multiple visibility criteria, every criterion must be fulfilled.'
              )
              .optional(),
            sectionIds: z
              .object({ values: z.array(z.string()).optional() })
              .describe('IDs of the sections that are included in the menu.')
              .optional(),
            archived: z
              .boolean()
              .describe(
                "Whether the menu is archived. Defaults to `false`. **Note:** Archived menus can't be updated."
              )
              .optional()
              .nullable(),
          })
          .describe('Menu.')
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk Unarchive Menus metadata.')
    .optional(),
});
